import React, { useState, useEffect, useRef } from 'react';
import CameraCompanion from './CameraCompanion';
import { joinRoom, getSocket } from './roomClient';
import { emptyVisit, formatDart, visitTotal, threeDartAverage } from './dartboardMath';
import {
  announce501Visit,
  announceGolfHole,
  announceShanghaiVisit,
  announceTurn,
  listVoices,
  loadAnnouncerPrefs,
  saveAnnouncerPrefs,
  cancelSpeech,
  speak,
  testAllAnnouncer,
} from './announcer';
import { getCheckoutRoutes, checkoutHintFor } from './checkout';
import { interpretRoomState, commitScoreboardRoom } from './syncGuards';

const APP_VERSION = '9.4.5';
const DEFAULT_ROOM = 'HOME';

const VALID_MAX_SCORE = 180;
const ADMIN_PIN = "1710";

const COURSE_LAYOUTS = {
  paul: { name: "Psycho Sanctuary", pars: { 1:4, 2:4, 3:4, 4:4, 5:4, 6:4, 7:4, 8:4, 9:4, 10:4, 11:4, 12:4, 13:4, 14:4, 15:4, 16:4, 17:4, 18:4, 19:4, 20:4 } },
  bernie: { name: "Mottly Reserve", pars: { 1:4, 2:5, 3:3, 4:4, 5:4, 6:3, 7:3, 8:4, 9:4, 10:5, 11:4, 12:4, 13:3, 14:3, 15:4, 16:5, 17:4, 18:4, 19:4, 20:4 } }
};

const TOURNAMENT_NAMES = { none: "Casual Play", masters: "Northern Rivers Masters", classic: "Currumbin Classic" };

const GOLF_RESULTS = [
  { label: 'ALBATROSS (-3)', relativeScore: -3, color: '#6f42c1', text: '#fff' }, 
  { label: 'EAGLE (-2)', relativeScore: -2, color: '#ffc107', text: '#000' },
  { label: 'BIRDIE (-1)', relativeScore: -1, color: '#28a745', text: '#fff' }, 
  { label: 'PAR (E)', relativeScore: 0, color: '#6c757d', text: '#fff' },
  { label: 'BOGEY (+1)', relativeScore: 1, color: '#fd7e14', text: '#fff' }, 
  { label: 'DBL BOGEY (+2)', relativeScore: 2, color: '#8b4513', text: '#fff' },
  { label: 'TRIPLE (+3)', relativeScore: 3, color: '#dc3545', text: '#fff' }, 
  { label: 'QUAD (+4)', relativeScore: 4, color: '#8b0000', text: '#fff' }
];

const formatScore = (s) => s === 0 ? 'E' : s > 0 ? `+${s}` : s;

// Astra house Shanghai: 11 rounds. Numbered: SINGLE=n DOUBLE=2n TRIPLE=3n.
const SH_ROUNDS = [
  { type: 'num', label: '14' },
  { type: 'num', label: '15' },
  { type: 'anyD', label: 'ANY DOUBLE' },
  { type: 'num', label: '16' },
  { type: 'num', label: '17' },
  { type: 'anyT', label: 'ANY TRIPLE' },
  { type: 'num', label: '18' },
  { type: 'num', label: '19' },
  { type: 'outer', label: '25' },
  { type: 'num', label: '20' },
  { type: 'bull', label: 'BULL' },
];
const SH_LAST = SH_ROUNDS.length - 1;
const SH_DOUBLE_VALS = Array.from({ length: 20 }, (_, i) => (i + 1) * 2);
const SH_TRIPLE_VALS = Array.from({ length: 20 }, (_, i) => (i + 1) * 3);
const shVisitCombos = (vals) => {
  const out = new Set();
  vals.forEach(a => {
    out.add(a);
    vals.forEach(b => {
      out.add(a + b);
      vals.forEach(c => out.add(a + b + c));
    });
  });
  return out;
};
const SH_VALID_DOUBLE_SCORES = shVisitCombos(SH_DOUBLE_VALS);
const SH_VALID_TRIPLE_SCORES = shVisitCombos(SH_TRIPLE_VALS);
const shTypedScoreOk = (type, n) => {
  if (!n) return false;
  if (type === 'anyD') return SH_VALID_DOUBLE_SCORES.has(n);
  if (type === 'anyT') return SH_VALID_TRIPLE_SCORES.has(n);
  return true;
};

const SH_PTS = { M: 0, S: 1, D: 2, T: 3, O: 25, B: 50 };
const SH_DART_LABEL = { M: 'M', S: 'S', D: 'D', T: 'T', O: '25', B: '50' };
const emptyShanghaiStats = () => ({
  wins: {},
  shanghaiHits: {},
  highVisit: {},
  highGame: {},
  doubles: {},
  triples: {},
  singles: {},
  bulls: {},
  games: []
});
const ensureShanghaiStats = (statsObj) => {
  if (!statsObj.shanghai) statsObj.shanghai = emptyShanghaiStats();
  const blank = emptyShanghaiStats();
  Object.keys(blank).forEach(k => {
    if (k === 'games') {
      if (!Array.isArray(statsObj.shanghai.games)) statsObj.shanghai.games = [];
      return;
    }
    if (!statsObj.shanghai[k] || typeof statsObj.shanghai[k] !== 'object' || Array.isArray(statsObj.shanghai[k])) statsObj.shanghai[k] = {};
  });
  return statsObj.shanghai;
};
const addMapCounts = (dest, src) => {
  Object.entries(src || {}).forEach(([name, n]) => {
    if (typeof n === 'number' && n > 0) dest[name] = (dest[name] || 0) + n;
  });
};
const rebuildShanghaiFromGames = (games) => {
  const next = emptyShanghaiStats();
  next.games = games || [];
  next.games.forEach(g => {
    (g.winners || []).forEach(n => {
      next.wins[n] = (next.wins[n] || 0) + 1;
      if (g.instant) next.shanghaiHits[n] = (next.shanghaiHits[n] || 0) + 1;
    });
    Object.entries(g.totals || {}).forEach(([n, t]) => {
      if (typeof t === 'number' && t > (next.highGame[n] || 0)) next.highGame[n] = t;
    });
    Object.entries(g.highVisit || {}).forEach(([n, t]) => {
      if (typeof t === 'number' && t > (next.highVisit[n] || 0)) next.highVisit[n] = t;
    });
    addMapCounts(next.doubles, g.doubles);
    addMapCounts(next.triples, g.triples);
    addMapCounts(next.singles, g.singles);
    addMapCounts(next.bulls, g.bulls);
  });
  return next;
};
const shanghaiDartCounts = (darts) => {
  let singles = 0, doubles = 0, triples = 0, bulls = 0;
  (darts || []).forEach(d => {
    if (d === 'S') singles += 1;
    else if (d === 'D' || (typeof d === 'string' && /^D\d+$/.test(d))) doubles += 1;
    else if (d === 'T' || (typeof d === 'string' && /^T\d+$/.test(d))) triples += 1;
    else if (d === 'B') bulls += 1;
  });
  return { singles, doubles, triples, bulls };
};
const shanghaiLeader = (map) => {
  let bestName = null;
  let bestVal = 0;
  Object.entries(map || {}).forEach(([name, val]) => {
    if (typeof val === 'number' && val > bestVal) {
      bestName = name;
      bestVal = val;
    }
  });
  return bestName ? { player: bestName, value: bestVal } : null;
};
const shHelpText = (rnd) => {
  if (!rnd) return '';
  if (rnd.type === 'num') {
    const n = parseInt(rnd.label, 10) || 0;
    return `Single ${n} · Double ${n * 2} · Triple ${n * 3} · Miss the lot halves`;
  }
  if (rnd.type === 'anyD') return 'Any double · type a real double score · max 120 · miss all halves';
  if (rnd.type === 'anyT') return 'Any triple · type a real triple score · max 180 · miss all halves';
  if (rnd.type === 'outer') return 'Outer 25 · red bull 50 · miss both halves';
  if (rnd.type === 'bull') return 'Red bull 50 · miss the red halves';
  return '';
};
const shDartPts = (d, n = 0) => {
  if (d === 'O') return SH_PTS.O;
  if (d === 'B') return SH_PTS.B;
  if (d === 'M') return SH_PTS.M;
  if (d === 'S' || d === 'D' || d === 'T') return (SH_PTS[d] || 0) * n;
  if (typeof d === 'string' && /^D\d+$/.test(d)) {
    const v = parseInt(d.slice(1), 10);
    return Number.isFinite(v) ? SH_PTS.D * v : 0;
  }
  if (typeof d === 'string' && /^T\d+$/.test(d)) {
    const v = parseInt(d.slice(1), 10);
    return Number.isFinite(v) ? SH_PTS.T * v : 0;
  }
  return 0;
};
const isShHit = (d) => {
  if (d === 'S' || d === 'D' || d === 'T' || d === 'O' || d === 'B') return true;
  return typeof d === 'string' && /^[DT]\d+$/.test(d);
};

const getHoleColor = (score) => {
    if (score === undefined || score === null) return 'transparent';
    if (score <= -2) return '#ffc107'; 
    if (score === -1) return '#28a745'; 
    if (score === 0) return '#6c757d'; 
    if (score === 1) return '#fd7e14'; 
    if (score >= 2) return '#dc3545'; 
    return 'transparent';
};

const apiSave = async (key, data) => {
    if (data === null) {
        await fetch(`/api/store/${key}`, { method: 'DELETE' });
    } else {
        await fetch(`/api/store/${key}`, { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify(data) 
        });
    }
};

export default function App() {
  if (typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') === '/camera') {
    return <CameraCompanion />;
  }
  return <DartsScorer />;
}

function DartsScorer() {
  const [isReady, setIsReady] = useState(false);
  const [activeMode, setActiveMode] = useState('menu'); 
  const [playerCount, setPlayerCount] = useState(4);
  const [playerNames, setPlayerNames] = useState(['Bruce M', 'Paul S', 'Rod B', 'Player 4']);
  const [selectedCourse, setSelectedCourse] = useState('paul');
  const [tournament, setTournament] = useState('none');
  const [modal, setModal] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const [adminOpen, setAdminOpen] = useState(false);
  const [postRoundData, setPostRoundData] = useState(null);
  
  const [analTab, setAnalTab] = useState('ledger');
  const [expandedCard, setExpandedCard] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [suspended501, setSuspended501] = useState(null);
  const [suspendedGolf, setSuspendedGolf] = useState(null);
  const [suspendedShanghai, setSuspendedShanghai] = useState(null);
  
  const [stats, setStats] = useState({ 
      '501': { wins: {}, highOut: {} }, 
      'golf': { wins: {}, courseRecords: {}, tournamentRecords: {}, albatrosses: {}, eagles: {}, birdies: {}, pars: {} },
      'shanghai': emptyShanghaiStats(),
      'scorecards': []
  });

  const [p501, setP501] = useState([]);
  const [active501Idx, setActive501Idx] = useState(0);
  const [input501, setInput501] = useState('');
  const [history501, setHistory501] = useState([]);
  const [roomId, setRoomId] = useState(DEFAULT_ROOM);
  const [roomDraft, setRoomDraft] = useState(DEFAULT_ROOM);
  const [roomPeer, setRoomPeer] = useState(false);
  const [cameraConnected, setCameraConnected] = useState(false);
  const [cameraCount, setCameraCount] = useState(0);
  const [socketOk, setSocketOk] = useState(false);
  const [visit501, setVisit501] = useState(emptyVisit());
  const [visitEditIdx, setVisitEditIdx] = useState(0);
  const [announcerPrefs, setAnnouncerPrefs] = useState(() => loadAnnouncerPrefs());
  const [voiceList, setVoiceList] = useState([]);
  const [announcerOpen, setAnnouncerOpen] = useState(false);
  const lastAnnouncedTurnRef = useRef('');
  const skipPublishRef = useRef(false);
  const roomIdRef = useRef(roomId);
  const handledWinRef = useRef(null);
  const syncRef = useRef({ hydrated: false, seq: null });
  const activeModeRef = useRef(activeMode);
  const isReadyRef = useRef(isReady);
  roomIdRef.current = roomId;
  activeModeRef.current = activeMode;
  isReadyRef.current = isReady;
  
  const [golfPlayers, setGolfPlayers] = useState([]);
  const [activeGolfIdx, setActiveGolfIdx] = useState(0);
  const [currentHole, setCurrentHole] = useState(1);
  const [currentRound, setCurrentRound] = useState(1);
  const [golfHistory, setGolfHistory] = useState([]);
  const [golfPlayoff, setGolfPlayoff] = useState(false);
  const [playoffNames, setPlayoffNames] = useState([]);
  const [playoffLap, setPlayoffLap] = useState(1);

  const [shPlayers, setShPlayers] = useState([]);
  const [activeShIdx, setActiveShIdx] = useState(0);
  const [currentShRound, setCurrentShRound] = useState(0);
  const [inputSh, setInputSh] = useState('');
  const [shDarts, setShDarts] = useState([]);
  const [historySh, setHistorySh] = useState([]);
  const [shanghaiSplash, setShanghaiSplash] = useState(null);
  const [shanghaiDismissReady, setShanghaiDismissReady] = useState(false);

  useEffect(() => {
      const loadVault = async () => {
          try {
              const [resStats, res501, resGolf, resSh] = await Promise.all([
                  fetch('/api/store/astra_darts_ledger'), 
                  fetch('/api/store/astra_suspended_501'), 
                  fetch('/api/store/astra_suspended_golf'),
                  fetch('/api/store/astra_suspended_shanghai')
              ]);
              const dStats = await resStats.json();
              if (dStats) {
                  if (!dStats.golf.courseRecords) dStats.golf.courseRecords = {};
                  if (!dStats.golf.tournamentRecords) dStats.golf.tournamentRecords = {};
                  if (!dStats.golf.albatrosses) dStats.golf.albatrosses = {};
                  if (!dStats.golf.eagles) dStats.golf.eagles = {};
                  if (!dStats.golf.birdies) dStats.golf.birdies = {};
                  if (!dStats.golf.pars) dStats.golf.pars = {};
                  if (!dStats.shanghai) dStats.shanghai = emptyShanghaiStats();
                  ensureShanghaiStats(dStats);
                  if (!dStats.scorecards) dStats.scorecards = [];
                  setStats(dStats);
              }
              setSuspended501(await res501.json());
              setSuspendedGolf(await resGolf.json());
              setSuspendedShanghai(await resSh.json());
          } catch (e) { console.error("Database connection failed", e); }
          setIsReady(true);
      };
      loadVault();
  }, []);

  useEffect(() => { 
      if (isReady && activeMode !== 'analytics') apiSave('astra_darts_ledger', stats); 
  }, [stats, isReady, activeMode]);

  useEffect(() => {
      const syncVoices = () => setVoiceList(listVoices());
      syncVoices();
      if (typeof window !== 'undefined' && window.speechSynthesis) {
          window.speechSynthesis.onvoiceschanged = syncVoices;
      }
      return () => {
          if (typeof window !== 'undefined' && window.speechSynthesis) {
              window.speechSynthesis.onvoiceschanged = null;
          }
      };
  }, []);

  const updateAnnouncer = (patch) => {
      setAnnouncerPrefs(prev => {
          const next = { ...prev, ...patch };
          saveAnnouncerPrefs(next);
          if (next.muted) cancelSpeech();

          return next;
      });
  };

  // --- v2 live room (iPad scoreboard ↔ phone camera) ---
  useEffect(() => {
      syncRef.current = { hydrated: false, seq: null };
      const s = joinRoom(roomId, 'scoreboard');
      const seenWinSeq = () => {
          try { return Number(sessionStorage.getItem('astra_v2_seen_win_' + roomIdRef.current) || ''); } catch (_) { return NaN; }
      };
      const onState = (st) => {
          if (!st) return;
          if (st.presence) {
              setCameraConnected(!!st.presence.cameraConnected);
              setCameraCount(st.presence.cameraCount || 0);
          }
          const decided = interpretRoomState(syncRef.current, st, activeModeRef.current, {
              seenWinSeq: seenWinSeq(),
          });
          syncRef.current = decided.next;
          decided.actions.forEach((act) => {
              if (act.type === 'apply501' && act.state && act.state.p501) {
                  if (act.skipPublish && isReadyRef.current) skipPublishRef.current = true;
                  const snap = act.state;
                  setP501(snap.p501.map(pl => ({ ...pl })));
                  setActive501Idx(snap.active501Idx || 0);
                  setHistory501(snap.history501 || []);
                  setPlayerCount(snap.p501.length);
                  if (snap.playerNames) setPlayerNames(snap.playerNames);
              } else if (act.type === 'enter501') {
                  // Menu only. Golf / Shanghai / analytics must not be yanked.
                  if (activeModeRef.current === 'menu') setActiveMode('501');
              } else if (act.type === 'visit' && act.visit) {
                  setVisit501(act.visit.map(d => (d ? { ...d } : null)));
              } else if (act.type === 'announce') {
                  const ev = act.event;
                  const snap = act.state || st;
                  if (ev.type === 'win') {
                      const thrown = snap.winThrown || ev.thrown || 0;
                      const winKey = `win-${ev.seq}`;
                      if (handledWinRef.current === winKey) return;
                      handledWinRef.current = winKey;
                      try { sessionStorage.setItem('astra_v2_seen_win_' + roomIdRef.current, String(ev.seq)); } catch (_) {}
                      setStats(prev => {
                          const next = JSON.parse(JSON.stringify(prev));
                          if (!next['501'].avg) next['501'].avg = {};
                          if (!next['501'].visits) next['501'].visits = {};
                          next['501'].wins[snap.winner] = (next['501'].wins[snap.winner] || 0) + 1;
                          if (thrown > (next['501'].highOut[snap.winner] || 0)) next['501'].highOut[snap.winner] = thrown;
                          (snap.p501 || []).forEach((pl, i) => {
                            const a = threeDartAverage(snap.history501 || [], i);
                            if (a != null) next['501'].avg[pl.name] = a;
                            next['501'].visits[pl.name] = (snap.history501 || []).filter(h => h.playerIdx === i).length;
                          });
                          return next;
                      });
                      clearSuspended501();
                      announce501Visit({ player: snap.winner, thrown, win: true });
                  } else {
                      const evKey = `${ev.type}-${ev.seq}`;
                      if (handledWinRef.current === evKey) return;
                      handledWinRef.current = evKey;
                      const nextP = snap.p501?.[snap.active501Idx || 0];
                      announce501Visit({
                          player: ev.player,
                          thrown: ev.thrown,
                          remaining: ev.remaining,
                          bust: ev.type === 'bust',
                          nextPlayer: nextP?.name,
                          checkoutHint: ev.remaining ? checkoutHintFor(ev.remaining) : '',
                      });
                  }
              } else if (act.type === 'winAlert') {
                  const snap = act.state || st;
                  showAlert('Match Shot!', `${snap.winner} wins 501!`, () => {
                      if (activeModeRef.current === '501') setActiveMode('menu');
                  });
              }
          });
      };
      const onPeer = (peer) => {
          setRoomPeer(true);
          if (peer && peer.role === 'camera') setCameraConnected(true);
      };
      const onPresence = (pr) => {
          if (!pr) return;
          setCameraConnected(!!pr.cameraConnected);
          setCameraCount(pr.cameraCount || 0);
          setRoomPeer(!!(pr.cameraConnected || pr.scoreboardCount));
      };
      const onApplied = () => {
          // Scoring + announcer run from room:state (lastEvent). Announcing here
          // as well double-spoke checkout wins.
      };
      const onShanghaiCamera = (payload) => {
          try {
              window.dispatchEvent(new CustomEvent('astra-shanghai-camera', { detail: payload || {} }));
          } catch (_) {}
      };
      const onShanghaiUndo = () => {
          try {
              window.dispatchEvent(new CustomEvent('astra-shanghai-undo'));
          } catch (_) {}
      };
      const onGolfCamera = (payload) => {
          try {
              window.dispatchEvent(new CustomEvent('astra-golf-camera', { detail: payload || {} }));
          } catch (_) {}
      };
      const onGolfUndo = () => {
          try {
              window.dispatchEvent(new CustomEvent('astra-golf-undo'));
          } catch (_) {}
      };
      const onConnect = () => setSocketOk(true);
      const onDisconnect = () => { setSocketOk(false); /* keep last camera badge until presence says otherwise */ };
      s.on('room:state', onState);
      s.on('room:peer', onPeer);
      s.on('room:presence', onPresence);
      s.on('visit:applied', onApplied);
      s.on('shanghai:cameraConfirm', onShanghaiCamera);
      s.on('shanghai:undo', onShanghaiUndo);
      s.on('golf:cameraConfirm', onGolfCamera);
      s.on('golf:undo', onGolfUndo);
      s.on('connect', onConnect);
      s.on('disconnect', onDisconnect);
      setSocketOk(!!s.connected);
      return () => {
          s.off('room:state', onState);
          s.off('room:peer', onPeer);
          s.off('room:presence', onPresence);
          s.off('visit:applied', onApplied);
          s.off('shanghai:cameraConfirm', onShanghaiCamera);
          s.off('shanghai:undo', onShanghaiUndo);
          s.off('golf:cameraConfirm', onGolfCamera);
          s.off('golf:undo', onGolfUndo);
          s.off('connect', onConnect);
          s.off('disconnect', onDisconnect);
      };
  }, [roomId]);

  useEffect(() => {
      if (!isReady) return;
      if (skipPublishRef.current) {
          skipPublishRef.current = false;
          return;
      }
      if (activeMode !== '501' && activeMode !== 'menu' && activeMode !== 'shanghai' && activeMode !== 'golf') return;
      const modeOut = activeMode === '501' ? '501' : activeMode === 'shanghai' ? 'shanghai' : activeMode === 'golf' ? 'golf' : 'menu';
      const shRoundPub = SH_ROUNDS[currentShRound] || SH_ROUNDS[0];
      const course = COURSE_LAYOUTS[selectedCourse];
      const holePar = (course && course.pars && course.pars[currentHole]) || 4;
      getSocket().emit('room:publish', {
          roomId: roomIdRef.current,
          state: {
              mode: modeOut,
              playerCount,
              playerNames,
              p501,
              active501Idx,
              history501,
              shPlayers,
              activeShIdx,
              currentShRound,
              historySh,
              shDarts,
              shRound: shRoundPub,
              golfPlayers,
              activeGolfIdx,
              currentHole,
              currentRound,
              golfPar: holePar,
              golfCourseName: course ? course.name : '',
              golfPlayoff,
          },
      });
  }, [isReady, activeMode, playerCount, playerNames, p501, active501Idx, history501, shPlayers, activeShIdx, currentShRound, historySh, shDarts, golfPlayers, activeGolfIdx, currentHole, currentRound, selectedCourse, golfPlayoff]);

  useEffect(() => {
      let interval;
      if (activeMode === 'analytics' && autoRefresh) {
          interval = setInterval(async () => {
              try {
                  const resStats = await fetch('/api/store/astra_darts_ledger');
                  const dStats = await resStats.json();
                  if (dStats && dStats.golf) {
                      if (!dStats.scorecards) dStats.scorecards = [];
                      if (!dStats.shanghai) dStats.shanghai = emptyShanghaiStats();
                      ensureShanghaiStats(dStats);
                      setStats(dStats);
                  }
              } catch (e) { console.error("Auto-refresh failed", e); }
          }, 10000);
      }
      return () => clearInterval(interval);
  }, [activeMode, autoRefresh]);

  useEffect(() => {
      if (!shanghaiSplash) {
          setShanghaiDismissReady(false);
          return;
      }
      setShanghaiDismissReady(false);
      const prefs = loadAnnouncerPrefs();
      let cheer = null;
      if (!prefs.muted && prefs.soundCues !== false) {
          cheer = new Audio('/shanghai-cheer.mp3');
          cheer.volume = Math.max(0, Math.min(1, Number(prefs.volume ?? 0.9)));
          cheer.play().catch(() => {});
      }
      const readyTimer = setTimeout(() => setShanghaiDismissReady(true), 2000);
      const autoTimer = setTimeout(() => {
          setShanghaiSplash(null);
          setActiveMode('menu');
      }, 8000);
      return () => {
          if (cheer) {
              cheer.pause();
              cheer.currentTime = 0;
          }
          clearTimeout(readyTimer);
          clearTimeout(autoTimer);
      };
  }, [shanghaiSplash]);

  const dismissShanghaiSplash = () => {
      if (!shanghaiDismissReady) return;
      setShanghaiSplash(null);
      setActiveMode('menu');
  };

  const showAlert = (title, message, onConfirm = null) => setModal({ isOpen: true, type: 'alert', title, message, onConfirm });
  const showConfirm = (title, message, onConfirm) => setModal({ isOpen: true, type: 'confirm', title, message, onConfirm });
  const closeModal = () => setModal({ ...modal, isOpen: false });

  const openAdmin = () => {
      const pin = window.prompt("Enter Admin PIN:");
      if (pin !== ADMIN_PIN) {
          if (pin !== null) showAlert("Access Denied", "Incorrect PIN.");
          return;
      }
      setAdminOpen(true);
  };

  const persistStats = (next) => {
      setStats(next);
      apiSave('astra_darts_ledger', next);
  };

  const clearShanghaiRecords = () => {
      showConfirm("Clear all Shanghai games?", "Removes every saved Shanghai game and the Shanghai hero stats. The 501 game and Golf are not touched.", () => {
          persistStats({ ...stats, shanghai: emptyShanghaiStats() });
          showAlert("Shanghai cleared", "All Shanghai games are gone. Ready for the demo.");
      });
  };

  const deleteShanghaiGame = (id) => {
      const game = ((stats.shanghai && stats.shanghai.games) || []).find(g => g.id === id);
      const label = game ? `${game.date || ''} · ${game.title || 'Shanghai game'}` : 'this Shanghai game';
      showConfirm("Delete this Shanghai game?", `Remove ${label}? Only this game is deleted. The 501 game and Golf are not touched.`, () => {
          const games = ((stats.shanghai && stats.shanghai.games) || []).filter(g => g.id !== id);
          persistStats({ ...stats, shanghai: rebuildShanghaiFromGames(games) });
      });
  };

  const deleteGolfScorecard = (id) => {
      const card = (stats.scorecards || []).find(c => c.id === id);
      const label = card ? `${card.date || ''} ${COURSE_LAYOUTS[card.course]?.name || ''}`.trim() : 'this round';
      showConfirm("Delete this golf round?", `Remove ${label}? Only this golf round is deleted. Shanghai and the 501 game are not touched.`, () => {
          persistStats({ ...stats, scorecards: (stats.scorecards || []).filter(c => c.id !== id) });
      });
  };

  const resetEntireDatabase = () => {
      showConfirm("SYSTEM WIPE", "This will permanently delete ALL lifetime stats, Hall of Fame records, suspended games, and archived scorecards. This cannot be undone. Proceed?", () => {
          const emptyVault = { 
              '501': { wins: {}, highOut: {} }, 
              'golf': { wins: {}, courseRecords: {}, tournamentRecords: {}, albatrosses: {}, eagles: {}, birdies: {}, pars: {} },
              'shanghai': emptyShanghaiStats(),
              'scorecards': [] 
          };
          persistStats(emptyVault);
          setSuspended501(null);
          setSuspendedGolf(null);
          setSuspendedShanghai(null);
          apiSave('astra_suspended_501', null);
          apiSave('astra_suspended_golf', null);
          apiSave('astra_suspended_shanghai', null);
          setAdminOpen(false);
          showAlert("System Reset", "The database has been completely wiped clean. Ready for the real tournament.");
      });
  };

  // --- 501 LOGIC ---
  const suspend501 = () => {
    const data = { playerCount, playerNames, p501, active501Idx, history501 };
    setSuspended501(data); apiSave('astra_suspended_501', data);
    showAlert("Match Paused", "501 game saved securely.", () => setActiveMode('menu'));
  };

  const resume501 = () => {
    setPlayerCount(suspended501.playerCount); setPlayerNames(suspended501.playerNames); setP501(suspended501.p501); 
    setActive501Idx(suspended501.active501Idx); setHistory501(suspended501.history501); setActiveMode('501');
  };

  const clearSuspended501 = () => { setSuspended501(null); apiSave('astra_suspended_501', null); };

  const start501 = () => {
    const init = () => { 
        clearSuspended501(); 
        setP501(Array.from({ length: playerCount }, (_, i) => ({ id: i, name: playerNames[i] || `Player ${i + 1}`, currentScore: 501 }))); 
        setActive501Idx(0); setHistory501([]); setInput501(''); setActiveMode('501'); 
        lastAnnouncedTurnRef.current = '';
        handledWinRef.current = null;
        try { sessionStorage.removeItem('astra_v2_seen_win_' + roomIdRef.current); } catch (_) {}
    };
    if (suspended501) showConfirm("Overwrite 501?", "Starting a new 501 match deletes your saved one. Proceed?", init); else init();
  };

  const submit501Score = (val, dartsUsed = null) => {
    const score = parseInt(val, 10);
    if (isNaN(score) || score < 0 || score > VALID_MAX_SCORE) { showAlert("Invalid Entry", "Score must be 0-180."); return; }
    try { getSocket().emit('visit:clear', { roomId: roomIdRef.current }); } catch (_) {}

    const updated = p501.map(p => ({ ...p }));
    const cp = updated[active501Idx];
    const prevScore = cp.currentScore;
    const remaining = prevScore - score;
    const histExtra = dartsUsed ? { darts: dartsUsed } : {};
    const nextIdx = (active501Idx + 1) % playerCount;
    const nextName = updated[nextIdx]?.name || playerNames[nextIdx] || 'next';
    if (remaining < 0 || remaining === 1) { 
        setHistory501([...history501, { playerIdx: active501Idx, prev: prevScore, thrown: score, bust: true, ...histExtra }]); 
        setInput501(''); setVisit501(emptyVisit()); setVisitEditIdx(0);
        setActive501Idx(nextIdx);
        announce501Visit({ player: cp.name, thrown: score, bust: true, nextPlayer: nextName });
    }
    else if (remaining === 0) { 
        setStats(prev => { 
            const next = JSON.parse(JSON.stringify(prev));
            if (!next['501'].avg) next['501'].avg = {};
            if (!next['501'].visits) next['501'].visits = {};
            next['501'].wins[cp.name] = (next['501'].wins[cp.name] || 0) + 1;
            if(score > (next['501'].highOut[cp.name] || 0)) next['501'].highOut[cp.name] = score;
            const hist = [...history501, { playerIdx: active501Idx, prev: prevScore, thrown: score, bust: false, darts: dartsUsed || undefined }];
            p501.forEach((pl, i) => {
              const a = threeDartAverage(hist, i);
              if (a != null) next['501'].avg[pl.name] = a;
              next['501'].visits[pl.name] = (next['501'].visits[pl.name] || 0) + hist.filter(h => h.playerIdx === i).length;
            });
            return next;
        }); 
        clearSuspended501(); setVisit501(emptyVisit());
        announce501Visit({ player: cp.name, thrown: score, win: true });
        showAlert("Match Shot!", `${cp.name} wins 501!`, () => setActiveMode('menu')); 
    } else { 
        cp.currentScore = remaining;
        setP501(updated);
        setHistory501([...history501, { playerIdx: active501Idx, prev: prevScore, thrown: score, ...histExtra }]); 
        setInput501(''); setVisit501(emptyVisit()); setVisitEditIdx(0);
        setActive501Idx(nextIdx);
        announce501Visit({
          player: cp.name,
          thrown: score,
          remaining,
          nextPlayer: nextName,
          checkoutHint: remaining <= 170 ? checkoutHintFor(remaining) : '',
        });
    }
  };

  const confirmVisit501 = () => {
    const total = visitTotal(visit501);
    submit501Score(String(total), visit501.filter(Boolean));
  };

  const undo501 = () => {
    if (history501.length === 0) return;
    const last = history501[history501.length - 1]; const updated = [...p501];
    updated[last.playerIdx].currentScore = last.prev; setP501(updated); setActive501Idx(last.playerIdx); setHistory501(history501.slice(0, -1));
  };

  // --- GOLF LOGIC ---
  const suspendGolf = () => {
    const data = { playerCount, playerNames, selectedCourse, tournament, golfPlayers, activeGolfIdx, currentHole, currentRound, golfHistory, golfPlayoff, playoffNames, playoffLap };
    setSuspendedGolf(data); apiSave('astra_suspended_golf', data);
    showAlert("Match Paused", "Golf game saved securely.", () => setActiveMode('menu'));
  };

  const resumeGolf = () => {
    setPlayerCount(suspendedGolf.playerCount); setPlayerNames(suspendedGolf.playerNames); setSelectedCourse(suspendedGolf.selectedCourse); 
    setTournament(suspendedGolf.tournament); setGolfPlayers(suspendedGolf.golfPlayers); setActiveGolfIdx(suspendedGolf.activeGolfIdx); 
    setCurrentHole(suspendedGolf.currentHole); setCurrentRound(suspendedGolf.currentRound || 1); setGolfHistory(suspendedGolf.golfHistory);
    setGolfPlayoff(!!suspendedGolf.golfPlayoff); setPlayoffNames(suspendedGolf.playoffNames || []); setPlayoffLap(suspendedGolf.playoffLap || 1);
    setActiveMode('golf');
  };

  const clearSuspendedGolf = () => { setSuspendedGolf(null); apiSave('astra_suspended_golf', null); };

  const startGolf = () => {
    const init = () => { 
        clearSuspendedGolf(); 
        setGolfPlayers(Array.from({ length: playerCount }, (_, i) => ({ id: i, name: playerNames[i] || `Player ${i + 1}`, scores: {}, totalRelative: 0, tourTotal: 0, roundHistory: [] }))); 
        setActiveGolfIdx(0); setCurrentHole(1); setCurrentRound(1); setGolfHistory([]); setGolfPlayoff(false); setPlayoffNames([]); setPlayoffLap(1); setActiveMode('golf'); 
        if (tournament !== 'none') setSelectedCourse('paul');
    };
    if (suspendedGolf) showConfirm("Overwrite Golf?", "Starting a new Golf match deletes your saved one. Proceed?", init); else init();
  };

  const submitGolfScore = (relativeScore) => {
    const updated = golfPlayers.map(p => ({ ...p, scores: { ...p.scores }, roundHistory: [...p.roundHistory] })); 
    const cp = updated[activeGolfIdx];
    const scoreKey = golfPlayoff ? `${currentHole}_${playoffLap}` : currentHole;
    
    // Immutable snapshot for bulletproof undo
    setGolfHistory([...golfHistory, { 
        playersSnapshot: golfPlayers.map(p => ({ ...p, scores: { ...p.scores }, roundHistory: [...p.roundHistory] })), 
        activeIdx: activeGolfIdx, 
        hole: currentHole,
        golfPlayoff,
        playoffNames: [...playoffNames],
        playoffLap
    }]);

    cp.scores[scoreKey] = relativeScore;
    // Playoff holes do not alter the 18-hole totalRelative (course records / scorecard)
    if (!golfPlayoff) {
        cp.totalRelative += relativeScore;
    }

    {
        const nextName = (() => {
            if (golfPlayoff) {
                const scoreKey2 = `${currentHole}_${playoffLap}`;
                const ni = updated.findIndex(pl => playoffNames.includes(pl.name) && pl.scores[scoreKey2] === undefined && pl.name !== cp.name);
                if (ni >= 0) return updated[ni].name;
                return '';
            }
            if (activeGolfIdx + 1 < playerCount) return updated[activeGolfIdx + 1]?.name || '';
            return updated[0]?.name || '';
        })();
        const fmt = (s) => (s === 0 ? 'E' : s > 0 ? `+${s}` : String(s));
        announceGolfHole({
            player: cp.name,
            relativeScore,
            hole: currentHole,
            par: (COURSE_LAYOUTS[selectedCourse]?.pars || {})[currentHole] || 4,
            nextPlayer: nextName,
            totalLabel: fmt(cp.totalRelative),
        });
    }
    
    setStats(prev => { 
        const next = JSON.parse(JSON.stringify(prev));
        if (relativeScore === -3) next.golf.albatrosses[cp.name] = (next.golf.albatrosses[cp.name] || 0) + 1;
        if (relativeScore === -2) next.golf.eagles[cp.name] = (next.golf.eagles[cp.name] || 0) + 1;
        if (relativeScore === -1) next.golf.birdies[cp.name] = (next.golf.birdies[cp.name] || 0) + 1;
        if (relativeScore === 0) next.golf.pars[cp.name] = (next.golf.pars[cp.name] || 0) + 1;
        return next; 
    });

    if (golfPlayoff) {
        const nextIdx = updated.findIndex(p => playoffNames.includes(p.name) && p.scores[scoreKey] === undefined);
        if (nextIdx !== -1) {
            setGolfPlayers(updated);
            setActiveGolfIdx(nextIdx);
            return;
        }
        // All remaining playoff players scored this hole — sudden death on this hole alone
        const field = updated.filter(p => playoffNames.includes(p.name));
        const bestOnHole = Math.min(...field.map(p => p.scores[scoreKey]));
        const stillTied = field.filter(p => p.scores[scoreKey] === bestOnHole);
        if (stillTied.length === 1) {
            setGolfPlayers(updated);
            setGolfPlayoff(false);
            setPostRoundData({
                isTournamentEnd: true,
                players: updated,
                round: currentRound,
                course: selectedCourse,
                playoffWinner: stillTied[0].name
            });
            return;
        }
        const nextNames = stillTied.map(p => p.name);
        updated.sort((a, b) => {
            const aIn = nextNames.includes(a.name);
            const bIn = nextNames.includes(b.name);
            if (aIn && bIn) return (a.scores[scoreKey] ?? 0) - (b.scores[scoreKey] ?? 0);
            if (aIn !== bIn) return aIn ? -1 : 1;
            return 0;
        });
        const nextHole = currentHole === 19 ? 20 : 19;
        const nextLap = currentHole === 20 ? playoffLap + 1 : playoffLap;
        setPlayoffNames(nextNames);
        setGolfPlayers(updated);
        setCurrentHole(nextHole);
        setPlayoffLap(nextLap);
        setActiveGolfIdx(updated.findIndex(p => nextNames.includes(p.name)));
        return;
    }

    if (activeGolfIdx + 1 < playerCount) {
        setGolfPlayers(updated);
        setActiveGolfIdx(activeGolfIdx + 1);
    } else {
        if (currentHole >= 18) {
            const isTourneyEnd = tournament !== 'none' && currentRound === 4;
            const isCasualEnd = tournament === 'none';
            if (isTourneyEnd || isCasualEnd) {
                const getFinal = (p) => tournament !== 'none' ? p.tourTotal + p.totalRelative : p.totalRelative;
                const best = Math.min(...updated.map(getFinal));
                const tied = updated.filter(p => getFinal(p) === best);
                if (tied.length >= 2) {
                    const names = tied.map(p => p.name);
                    updated.sort((a, b) => {
                        const aIn = names.includes(a.name);
                        const bIn = names.includes(b.name);
                        if (aIn && bIn) return (a.scores[18] ?? 0) - (b.scores[18] ?? 0);
                        if (aIn !== bIn) return aIn ? -1 : 1;
                        return 0;
                    });
                    setGolfPlayers(updated);
                    setGolfPlayoff(true);
                    setPlayoffNames(names);
                    setPlayoffLap(1);
                    setCurrentHole(19);
                    setActiveGolfIdx(updated.findIndex(p => names.includes(p.name)));
                    return;
                }
            }
            setGolfPlayers(updated);
            setPostRoundData({ isTournamentEnd: isTourneyEnd || isCasualEnd, players: updated, round: currentRound, course: selectedCourse });
        } else { 
            // Honors System: Stable sort based on the hole just completed
            updated.sort((a, b) => a.scores[currentHole] - b.scores[currentHole]);
            setGolfPlayers(updated);
            setCurrentHole(currentHole + 1); 
            setActiveGolfIdx(0); 
        }
    }
  };

  const handlePostRoundContinue = () => {
      const isEnd = postRoundData.isTournamentEnd;
      const snapshotPlayers = postRoundData.players.map(p => ({ name: p.name, scores: { ...p.scores }, totalRelative: p.totalRelative }));

      setStats(prev => { 
          const next = JSON.parse(JSON.stringify(prev)); 
          const newCard = {
              id: Date.now().toString() + Math.random().toString(),
              date: new Date().toLocaleString(),
              course: postRoundData.course,
              tournament: tournament,
              round: postRoundData.round,
              players: snapshotPlayers
          };
          next.scorecards = [newCard, ...(next.scorecards || [])].slice(0, 50); 
          
          postRoundData.players.forEach(p => {
              const cr = next.golf.courseRecords[postRoundData.course];
              if (!cr || p.totalRelative < cr.score) {
                  next.golf.courseRecords[postRoundData.course] = { player: p.name, score: p.totalRelative };
              }
          });

          if (isEnd) {
              const getFinal = (p) => tournament !== 'none' ? p.tourTotal + p.totalRelative : p.totalRelative;
              const sorted = [...postRoundData.players].sort((a,b) => getFinal(a) - getFinal(b));
              const winner = postRoundData.playoffWinner
                  ? (postRoundData.players.find(p => p.name === postRoundData.playoffWinner) || sorted[0])
                  : sorted[0];
              const winningScore = getFinal(winner);
              
              if(!next.golf.wins) next.golf.wins = {};
              next.golf.wins[winner.name] = (next.golf.wins[winner.name] || 0) + 1;
              if (tournament !== 'none') {
                  const tr = next.golf.tournamentRecords[tournament];
                  if (!tr || winningScore < tr.score) {
                      next.golf.tournamentRecords[tournament] = { player: winner.name, score: winningScore };
                  }
              }
          }
          return next; 
      });

      if (isEnd) {
          clearSuspendedGolf(); setPostRoundData(null); setActiveMode('menu');
      } else {
          const nextRoundPlayers = postRoundData.players.map(p => ({
              ...p,
              tourTotal: p.tourTotal + p.totalRelative,
              roundHistory: [...p.roundHistory, p.totalRelative],
              totalRelative: 0,
              scores: {}
          })).sort((a, b) => a.tourTotal - b.tourTotal);
          
          setGolfPlayers(nextRoundPlayers); setCurrentRound(currentRound + 1); setCurrentHole(1); setActiveGolfIdx(0); setGolfHistory([]); 
          setGolfPlayoff(false); setPlayoffNames([]); setPlayoffLap(1);
          const nextCourse = (currentRound + 1) % 2 === 0 ? 'bernie' : 'paul';
          setSelectedCourse(nextCourse); setPostRoundData(null);
      }
  };

  const undoGolf = () => {
    if (golfHistory.length === 0) return;
    const last = golfHistory[golfHistory.length - 1]; 
    setGolfPlayers(last.playersSnapshot);
    setActiveGolfIdx(last.activeIdx);
    setCurrentHole(last.hole);
    setGolfPlayoff(!!last.golfPlayoff);
    setPlayoffNames(last.playoffNames || []);
    setPlayoffLap(last.playoffLap || 1);
    setGolfHistory(golfHistory.slice(0, -1));
  };

  const speakBoard = (text) => {
    speak(text, announcerPrefs);
  };



  useEffect(() => {
      if (activeMode !== '501' || !p501.length) return;
      const cp = p501[active501Idx];
      if (!cp) return;
      const key = `${cp.name}-${cp.currentScore}-${active501Idx}-${history501.length}`;
      if (lastAnnouncedTurnRef.current === key) return;
      // Skip the very first mount announce flood: only after at least one visit or when coming from camera
      if (history501.length === 0 && lastAnnouncedTurnRef.current === '') {
          lastAnnouncedTurnRef.current = key;
          announceTurn(cp.name, cp.currentScore, checkoutHintFor(cp.currentScore));
          return;
      }
      lastAnnouncedTurnRef.current = key;
  }, [activeMode, active501Idx, p501, history501.length]);

  // --- SHANGHAI LOGIC ---
  // House: 11-round sequence. Numbered: S=n D=2n T=3n. Any D/T use the tapped number.
  const shRound = SH_ROUNDS[currentShRound] || SH_ROUNDS[0];
  const shTargetN = (rnd = shRound) => (rnd && rnd.type === 'num') ? (parseInt(rnd.label, 10) || 0) : 0;

  const suspendShanghai = () => {
    const data = { playerCount, playerNames, shPlayers, activeShIdx, currentShRound, historySh };
    setSuspendedShanghai(data); apiSave('astra_suspended_shanghai', data);
    showAlert("Match Paused", "Shanghai game saved securely.", () => setActiveMode('menu'));
  };

  const resumeShanghai = () => {
    setPlayerCount(suspendedShanghai.playerCount); setPlayerNames(suspendedShanghai.playerNames);
    const hist = suspendedShanghai.historySh || [];
    let players = (suspendedShanghai.shPlayers || []).map(p => ({ ...p }));
    // Prefer new currentShRound. Old 1-20 saves with empty history start on 14.
    let roundIdx = 0;
    if (typeof suspendedShanghai.currentShRound === 'number') {
        roundIdx = suspendedShanghai.currentShRound;
    }
    if (hist.length === 0 && players.length > 0 && players.every(p => (p.total ?? 0) === 0)) {
        players = players.map(p => ({ ...p, total: 20 }));
    }
    setShPlayers(players); setActiveShIdx(suspendedShanghai.activeShIdx);
    setCurrentShRound(roundIdx); setHistorySh(hist);
    setInputSh(''); setShDarts([]); setActiveMode('shanghai');
  };

  const clearSuspendedShanghai = () => { setSuspendedShanghai(null); apiSave('astra_suspended_shanghai', null); };

  const startShanghai = () => {
    const init = () => {
        clearSuspendedShanghai();
        setShPlayers(Array.from({ length: playerCount }, (_, i) => ({ id: i, name: playerNames[i] || `Player ${i + 1}`, total: 20 })));
        setActiveShIdx(0); setCurrentShRound(0); setHistorySh([]); setInputSh(''); setShDarts([]); setActiveMode('shanghai');
    };
    if (suspendedShanghai) showConfirm("Overwrite Shanghai?", "Starting a new Shanghai match deletes your saved one. Proceed?", init); else init();
  };

  const applyShanghaiVisit = (visitScore, dartsUsed, isShanghaiHit, didHalve) => {
    const R = currentShRound;
    const rnd = SH_ROUNDS[R] || SH_ROUNDS[0];
    const updated = shPlayers.map(p => ({ ...p }));
    const cp = updated[activeShIdx];
    const prevTotal = cp.total;
    if (didHalve) {
        cp.total = Math.floor(cp.total / 2);
    } else {
        cp.total += visitScore;
    }

    setHistorySh([...historySh, {
        playerIdx: activeShIdx,
        prevTotal,
        round: R,
        label: rnd.label,
        thrown: didHalve ? 0 : visitScore,
        darts: [...dartsUsed],
        halved: !!didHalve
    }]);

    const lastPlayer = activeShIdx + 1 >= playerCount;
    const gameOver = lastPlayer && R >= SH_LAST;
    const best = gameOver ? Math.max(...updated.map(p => p.total)) : 0;
    const winners = gameOver ? updated.filter(p => p.total === best).map(p => p.name) : [];
    const nextName = (lastPlayer ? updated[0] : updated[activeShIdx + 1])?.name || 'the next player';
    announceShanghaiVisit({
        player: cp.name,
        thrown: didHalve ? 0 : visitScore,
        total: gameOver ? best : cp.total,
        halved: !!didHalve,
        shanghai: !!isShanghaiHit,
        gameOver: !!gameOver,
        winners,
        nextPlayer: nextName,
        target: rnd.label,
    });

    if (isShanghaiHit || gameOver) {
        const histAll = [...historySh, {
            playerIdx: activeShIdx,
            thrown: didHalve ? 0 : visitScore,
            darts: [...dartsUsed],
            halved: !!didHalve
        }];
        const doubles = {}, triples = {}, singles = {}, bulls = {}, highVisit = {};
        histAll.forEach(h => {
            const pname = (updated[h.playerIdx] || shPlayers[h.playerIdx] || {}).name;
            if (!pname) return;
            const counts = shanghaiDartCounts(h.darts);
            if (counts.singles) singles[pname] = (singles[pname] || 0) + counts.singles;
            if (counts.doubles) doubles[pname] = (doubles[pname] || 0) + counts.doubles;
            if (counts.triples) triples[pname] = (triples[pname] || 0) + counts.triples;
            if (counts.bulls) bulls[pname] = (bulls[pname] || 0) + counts.bulls;
            if (!h.halved && h.thrown > (highVisit[pname] || 0)) highVisit[pname] = h.thrown;
        });
        const gameWinners = isShanghaiHit ? [cp.name] : winners;
        const card = {
            id: Date.now(),
            date: new Date().toLocaleString(),
            instant: !!isShanghaiHit,
            target: rnd.label,
            title: isShanghaiHit ? `Shanghai · ${cp.name} on ${rnd.label}` : `${gameWinners.join(' & ')} · finished`,
            winners: gameWinners,
            totals: Object.fromEntries(updated.map(p => [p.name, p.total])),
            doubles, triples, singles, bulls, highVisit
        };
        setStats(prev => {
            const next = JSON.parse(JSON.stringify(prev));
            ensureShanghaiStats(next);
            next.shanghai = rebuildShanghaiFromGames([...(next.shanghai.games || []), card]);
            return next;
        });
    }

    setInputSh('');
    setShDarts([]);

    if (isShanghaiHit) {
        setShPlayers(updated);
        clearSuspendedShanghai();
        setShanghaiSplash({ name: cp.name, target: rnd.label });
        return;
    }

    if (gameOver) {
        setShPlayers(updated);
        clearSuspendedShanghai();
        const label = winners.length > 1 ? `${winners.join(' & ')} tie` : `${winners[0]} wins`;
        showAlert("Shanghai Complete", `${label} with ${best} points.`, () => setActiveMode('menu'));
        return;
    }

    setShPlayers(updated);
    if (lastPlayer) {
        setCurrentShRound(R + 1);
        setActiveShIdx(0);
    } else {
        setActiveShIdx(activeShIdx + 1);
    }
  };

  const addShanghaiDart = (kind) => {
    const hasHit = shDarts.some(isShHit);

    // MISS with no scoring hits yet = missed the lot → half and next player. Not a Shanghai.
    if (kind === 'M') {
        if (!hasHit) {
            applyShanghaiVisit(0, ['M'], false, true);
        }
        // MISS after hits already recorded: ignore; they hit ENT to submit.
        return;
    }

    if (shDarts.length >= 3) return;
    const next = [...shDarts, kind];
    const visitScore = next.reduce((s, d) => s + shDartPts(d, shTargetN()), 0);
    setShDarts(next);
    setInputSh(String(visitScore));
    // Do not auto-submit after the 3rd dart. ENT is the only submit.
  };

  const submitShanghaiVisit = () => {
    if (shRound.type === 'anyD' || shRound.type === 'anyT') {
        const typed = parseInt(inputSh, 10);
        if (!typed) {
            applyShanghaiVisit(0, ['M'], false, true);
            return;
        }
        if (!shTypedScoreOk(shRound.type, typed)) {
            const kind = shRound.type === 'anyD' ? 'double' : 'triple';
            const max = shRound.type === 'anyD' ? 120 : 180;
            showAlert('Not a valid score', `${typed} is not a possible ${kind} visit. Max is ${max} (three ${shRound.type === 'anyD' ? 'double 20s' : 'triple 20s'}).`);
            return;
        }
        applyShanghaiVisit(typed, [shRound.type === 'anyD' ? 'D' : 'T'], false, false);
        return;
    }
    const hasHit = shDarts.some(isShHit);
    if (!hasHit) {
        // Empty visit (or ENT with no hits) = miss the lot. Not a Shanghai.
        applyShanghaiVisit(0, shDarts.length ? [...shDarts] : ['M'], false, true);
        return;
    }
    const visitScore = shDarts.reduce((s, d) => s + shDartPts(d, shTargetN()), 0);
    // Instant Shanghai only on numbered-target rounds.
    const isSh = shRound.type === 'num' && ['S', 'D', 'T'].every(x => shDarts.includes(x));
    applyShanghaiVisit(visitScore, [...shDarts], isSh, false);
  };

  // Phone camera → iPad Shanghai confirm / undo
  useEffect(() => {
      const onCam = (ev) => {
          if (activeMode !== 'shanghai') return;
          const kinds = (ev.detail && ev.detail.shanghaiKinds) || [];
          const rnd = SH_ROUNDS[currentShRound] || SH_ROUNDS[0];
          if (rnd.type === 'anyD' || rnd.type === 'anyT') {
              const score = (ev.detail && ev.detail.visit || [])
                  .filter(Boolean)
                  .reduce((s, d) => s + (typeof d.value === 'number' ? d.value : 0), 0);
              // Prefer mapped D#/T# kinds if present
              const mapped = kinds.filter((k) => typeof k === 'string' && /^[DT]\d+$/.test(k));
              if (mapped.length) {
                  const visitScore = mapped.reduce((s, d) => s + shDartPts(d, 0), 0);
                  if (!visitScore) applyShanghaiVisit(0, ['M'], false, true);
                  else applyShanghaiVisit(visitScore, mapped, false, false);
                  return;
              }
              if (!score) applyShanghaiVisit(0, ['M'], false, true);
              else if (!shTypedScoreOk(rnd.type, score)) showAlert('Not a valid score', `${score} is not a possible ${rnd.type === 'anyD' ? 'double' : 'triple'} visit.`);
              else applyShanghaiVisit(score, [rnd.type === 'anyD' ? 'D' : 'T'], false, false);
              return;
          }
          const dartsUsed = kinds.length ? kinds : ['M'];
          const hasHit = dartsUsed.some(isShHit);
          if (!hasHit) {
              applyShanghaiVisit(0, dartsUsed, false, true);
              return;
          }
          const visitScore = dartsUsed.reduce((s, d) => s + shDartPts(d, shTargetN()), 0);
          const isSh = rnd.type === 'num' && ['S', 'D', 'T'].every((x) => dartsUsed.includes(x));
          applyShanghaiVisit(visitScore, dartsUsed, isSh, false);
      };
      const onUndo = () => {
          if (activeMode === 'shanghai') undoShanghai();
      };
      window.addEventListener('astra-shanghai-camera', onCam);
      window.addEventListener('astra-shanghai-undo', onUndo);
      return () => {
          window.removeEventListener('astra-shanghai-camera', onCam);
          window.removeEventListener('astra-shanghai-undo', onUndo);
      };
  });

  // Phone → iPad Golf hole confirm / undo
  useEffect(() => {
      const onGolf = (ev) => {
          if (activeMode !== 'golf') return;
          const rel = ev.detail && Number(ev.detail.relativeScore);
          if (!Number.isFinite(rel)) return;
          submitGolfScore(rel);
      };
      const onGolfUndoEv = () => {
          if (activeMode !== 'golf') return;
          undoGolf();
      };
      window.addEventListener('astra-golf-camera', onGolf);
      window.addEventListener('astra-golf-undo', onGolfUndoEv);
      return () => {
          window.removeEventListener('astra-golf-camera', onGolf);
          window.removeEventListener('astra-golf-undo', onGolfUndoEv);
      };
  });

  const clearPendingShanghaiDart = () => {
    if (shRound.type === 'anyD' || shRound.type === 'anyT' || shDarts.length === 0) {
        setShDarts([]);
        setInputSh('');
        return;
    }
    const next = shDarts.slice(0, -1);
    setShDarts(next);
    const visitScore = next.reduce((s, d) => s + shDartPts(d, shTargetN()), 0);
    setInputSh(next.length ? String(visitScore) : '');
  };

  const undoShanghai = () => {
    // Undo last SUBMITTED visit only (including reversing a halve via prevTotal).
    if (historySh.length === 0) return;
    const last = historySh[historySh.length - 1];
    const updated = shPlayers.map(p => ({ ...p }));
    updated[last.playerIdx].total = last.prevTotal;
    setShPlayers(updated);
    setActiveShIdx(last.playerIdx);
    setCurrentShRound(typeof last.round === 'number' ? last.round : 0);
    setHistorySh(historySh.slice(0, -1));
    setInputSh('');
    setShDarts([]);
  };

  const getLiveTotal = (p) => tournament !== 'none' ? p.tourTotal + p.totalRelative : p.totalRelative;
  const activePar = COURSE_LAYOUTS[selectedCourse].pars[currentHole];
  const shMaps = stats.shanghai || {};
  const allLedgerNames = [...new Set([
      ...Object.keys(stats['501'].wins || {}),
      ...Object.keys(stats['501'].highOut || {}),
      ...Object.keys(stats.golf.wins || {}),
      ...Object.keys(stats.golf.pars || {}),
      ...Object.keys(shMaps.wins || {}),
      ...Object.keys(shMaps.shanghaiHits || {}),
      ...Object.keys(shMaps.highVisit || {}),
      ...Object.keys(shMaps.highGame || {}),
      ...Object.keys(shMaps.doubles || {}),
      ...Object.keys(shMaps.triples || {}),
      ...Object.keys(shMaps.singles || {}),
      ...Object.keys(shMaps.bulls || {}),
  ])];
  const filteredLedger = allLedgerNames.filter(name => name.toLowerCase().includes(searchQuery.toLowerCase()));
  const active501Player = p501[active501Idx];
  const checkoutRoutes = active501Player ? getCheckoutRoutes(active501Player.currentScore) : [];

  // --- REUSABLE UI COMPONENTS ---
  const renderRecordsSummary = () => {
      const sh = stats.shanghai || {};
      const fmtLead = (map) => {
          const L = shanghaiLeader(map);
          return L ? `${L.player} (${L.value})` : 'Unclaimed';
      };
      return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '25px' }}>
          <div style={{ background: '#252525', padding: '20px', borderRadius: '12px', borderLeft: '6px solid #fd7e14', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#ccc', fontSize: '20px' }}>Course Records (18 Holes)</h4>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize:'20px', marginBottom: '10px'}}>
                  <span>Psycho:</span>
                  <strong style={{color:'#fff'}}>{stats.golf.courseRecords?.paul ? `${stats.golf.courseRecords.paul.player} (${formatScore(stats.golf.courseRecords.paul.score)})` : 'Unclaimed'}</strong>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize:'20px'}}>
                  <span>Mottly:</span>
                  <strong style={{color:'#fff'}}>{stats.golf.courseRecords?.bernie ? `${stats.golf.courseRecords.bernie.player} (${formatScore(stats.golf.courseRecords.bernie.score)})` : 'Unclaimed'}</strong>
              </div>
          </div>
          <div style={{ background: '#252525', padding: '20px', borderRadius: '12px', borderLeft: '6px solid #007bff', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#ccc', fontSize: '20px' }}>Tour Records (72 Holes)</h4>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize:'20px', marginBottom: '10px'}}>
                  <span>Masters:</span>
                  <strong style={{color:'#fff'}}>{stats.golf.tournamentRecords?.masters ? `${stats.golf.tournamentRecords.masters.player} (${formatScore(stats.golf.tournamentRecords.masters.score)})` : 'Unclaimed'}</strong>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize:'20px'}}>
                  <span>Classic:</span>
                  <strong style={{color:'#fff'}}>{stats.golf.tournamentRecords?.classic ? `${stats.golf.tournamentRecords.classic.player} (${formatScore(stats.golf.tournamentRecords.classic.score)})` : 'Unclaimed'}</strong>
              </div>
          </div>
          <div style={{ background: '#252525', padding: '20px', borderRadius: '12px', borderLeft: '6px solid #198754', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#ccc', fontSize: '20px' }}>Shanghai Records</h4>
              {[
                  ['Most Shanghais', sh.shanghaiHits],
                  ['Highest game', sh.highGame],
                  ['Highest visit', sh.highVisit],
                  ['Most doubles', sh.doubles],
                  ['Most triples', sh.triples],
              ].map(([label, map], i, arr) => (
                  <div key={label} style={{display: 'flex', justifyContent: 'space-between', fontSize:'20px', marginBottom: i < arr.length - 1 ? '10px' : 0}}>
                      <span>{label}:</span>
                      <strong style={{color:'#fff'}}>{fmtLead(map)}</strong>
                  </div>
              ))}
          </div>
      </div>
      );
  };

  if (!isReady) return <div style={{ display: 'flex', height: '100vh', width: '100vw', alignItems: 'center', justifyContent: 'center', background: '#121212', color: '#007bff', fontSize: '32px', fontWeight: 'bold' }}>Connecting to Vault...</div>;

  return (
    <div style={{ padding: '15px', fontFamily: 'sans-serif', backgroundColor: '#121212', color: '#fff', height: '100vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: activeMode === 'analytics' || activeMode === 'menu' ? 'auto' : 'hidden' }}>
      
      {/* -------------------- MAIN MENU -------------------- */}
      {activeMode === 'menu' && (
        <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', background: '#1e1e1e', padding: '30px', borderRadius: '16px', boxSizing: 'border-box', overflowY: 'auto', maxHeight: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', gap: '16px' }}>
            <span style={{ color: '#0d6efd', fontSize: '52px', letterSpacing: '4px', fontWeight: '900' }}>ASTRA</span>
            <img src="/astra-logo.png" alt="" style={{ width: '168px', height: '168px', objectFit: 'contain', display: 'block' }} />
            <span style={{ color: '#0d6efd', fontSize: '52px', letterSpacing: '4px', fontWeight: '900' }}>DARTS</span>
          </div>
          <div style={{ textAlign: 'center', marginBottom: '18px', color: '#888', fontSize: '16px' }}>
            v{APP_VERSION} · ready-for-test · checkout announce stays once
            <div style={{
              margin: '12px auto 0', maxWidth: 720, padding: '10px 14px', borderRadius: 12,
              border: '1px solid #1b4332', background: 'linear-gradient(90deg,#0f2419,#122033)',
              color: '#b7e4c7', fontSize: 14, lineHeight: 1.45, textAlign: 'left',
            }}>
              <strong style={{ color: '#74c69d' }}>Ready for home test</strong>
              {' '}· Open this HTTPS page on the iPad · spare phone → Camera (room HOME) · Calibrate → Baseline → Detect → 2-tap fix → Confirm.
              Still <em>assist</em> (not DartsOn). Mute all / Test voice on Announcer settings. v1 untouched on :4000.
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ color: '#aaa' }}>Room</span>
              <input
                value={roomDraft}
                onChange={(e) => {
                  const edited = commitScoreboardRoom(e.target.value, roomId, false);
                  setRoomDraft(edited.draft);
                }}
                onBlur={() => {
                  const next = commitScoreboardRoom(roomDraft, roomIdRef.current, true);
                  roomIdRef.current = next.roomId;
                  setRoomDraft(next.draft);
                  if (next.committed) setRoomId(next.roomId);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                style={{ width: 110, padding: '8px 10px', borderRadius: 8, border: 'none', background: '#111', color: '#fff', fontWeight: 700, letterSpacing: 1, textAlign: 'center' }}
              />
              <button type="button" onClick={() => {
                const next = commitScoreboardRoom(roomDraft, roomIdRef.current, true);
                roomIdRef.current = next.roomId;
                setRoomDraft(next.draft);
                if (next.committed) setRoomId(next.roomId);
              }} style={{ padding: '10px 14px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Join</button>
              <a href={`/camera?room=${encodeURIComponent(roomId)}`} style={{ padding: '10px 16px', background: '#28a745', color: '#fff', borderRadius: 8, fontWeight: 700, textDecoration: 'none' }}>Open Camera Phone →</a>
              <button
                type="button"
                onClick={async () => {
                  const url = `${window.location.origin}/camera?room=${encodeURIComponent(roomId)}`;
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: 'Astra Darts camera', text: `Join room ${roomId}`, url });
                    } else if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(url);
                      window.alert(`Camera link copied:\n${url}`);
                    } else {
                      window.prompt('Copy camera link', url);
                    }
                  } catch (e) {
                    if (e && e.name === 'AbortError') return;
                    try {
                      await navigator.clipboard.writeText(url);
                      window.alert(`Camera link copied:\n${url}`);
                    } catch (_) {
                      window.prompt('Copy camera link', url);
                    }
                  }
                }}
                style={{ padding: '14px 22px', background: '#0d6efd', color: '#fff', borderRadius: 10, fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 16, boxShadow: '0 6px 18px rgba(13,110,253,0.35)' }}
              >
                Share / copy camera link
              </button>
              <span style={{
                padding: '8px 12px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                background: cameraConnected ? '#143d24' : '#333',
                color: cameraConnected ? '#28a745' : '#aaa',
                border: cameraConnected ? '1px solid #28a745' : '1px solid #444',
              }}>
                {cameraConnected ? `Camera connected${cameraCount > 1 ? ` (${cameraCount})` : ''}` : 'Camera offline'}
              </span>
              <span style={{ fontSize: 13, color: socketOk ? '#8fbc8f' : '#dc3545' }}>{socketOk ? 'WS live' : 'WS reconnecting…'}</span>
              <button type="button" onClick={() => setAnnouncerOpen(o => !o)} style={{ padding: '10px 16px', background: '#333', color: '#fff', borderRadius: 8, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                {announcerOpen ? 'Hide announcer' : 'Announcer settings'}
              </button>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
              <img
                alt={`QR for room ${roomId}`}
                width={120}
                height={120}
                style={{ background: '#fff', borderRadius: 8, padding: 6 }}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(typeof window !== 'undefined' ? `${window.location.origin}/camera?room=${roomId}` : `/camera?room=${roomId}`)}`}
              />
              <div style={{ textAlign: 'left', fontSize: 13, color: '#9aa', maxWidth: 280 }}>
                <div style={{ color: '#ccc', fontWeight: 700, marginBottom: 4 }}>Phone QR · room {roomId}</div>
                Scan with the spare phone (same Wi‑Fi / HTTPS). Or open the green Camera link.
              </div>
            </div>
            {announcerOpen && (
              <div style={{ marginTop: 14, padding: 14, background: '#151515', borderRadius: 10, textAlign: 'left', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: '#ccc' }}>Voice &amp; announcer (501 + Shanghai + Golf)</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#ddd' }}>
                  <input type="checkbox" checked={!!announcerPrefs.muted} onChange={(e) => updateAnnouncer({ muted: e.target.checked })} />
                  Mute all (speech + sound cues)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#ddd' }}>
                  <input type="checkbox" checked={announcerPrefs.soundCues !== false} onChange={(e) => updateAnnouncer({ soundCues: e.target.checked })} />
                  Soft sound cues (180 / bust / win)
                </label>
                <label style={{ display: 'block', fontSize: 13, color: '#aaa', marginBottom: 4 }}>Voice</label>
                <select
                  value={announcerPrefs.voiceURI || ''}
                  onChange={(e) => updateAnnouncer({ voiceURI: e.target.value })}
                  style={{ width: '100%', padding: 8, borderRadius: 8, background: '#111', color: '#fff', border: '1px solid #333', marginBottom: 10 }}
                >
                  <option value="">Auto (en-AU / en-GB)</option>
                  {voiceList.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
                  ))}
                </select>
                <label style={{ display: 'block', fontSize: 13, color: '#aaa' }}>Rate {Number(announcerPrefs.rate).toFixed(2)}</label>
                <input type="range" min="0.7" max="1.3" step="0.05" value={announcerPrefs.rate} onChange={(e) => updateAnnouncer({ rate: Number(e.target.value) })} style={{ width: '100%' }} />
                <label style={{ display: 'block', fontSize: 13, color: '#aaa' }}>Pitch {Number(announcerPrefs.pitch).toFixed(2)}</label>
                <input type="range" min="0.7" max="1.4" step="0.05" value={announcerPrefs.pitch} onChange={(e) => updateAnnouncer({ pitch: Number(e.target.value) })} style={{ width: '100%' }} />
                <label style={{ display: 'block', fontSize: 13, color: '#aaa' }}>Volume {Math.round((announcerPrefs.volume ?? 1) * 100)}%</label>
                <input type="range" min="0" max="1" step="0.05" value={announcerPrefs.volume ?? 1} onChange={(e) => updateAnnouncer({ volume: Number(e.target.value) })} style={{ width: '100%' }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button type="button" onClick={() => speak('One hundred and eighty! Astra announcer ready.', announcerPrefs)} style={{ padding: '8px 14px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                    Test voice
                  </button>
                  <button type="button" onClick={() => testAllAnnouncer(announcerPrefs)} style={{ padding: '8px 14px', background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                    Test all phrases
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '25px' }}>
              {suspended501 && (
                  <button onClick={resume501} style={{ flex: 1, padding:'15px', backgroundColor:'#1a3a5a', border:'2px solid #007bff', borderRadius:'8px', color:'#fff', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>
                      Resume 501 Match
                  </button>
              )}
              {suspendedGolf && (
                  <button onClick={resumeGolf} style={{ flex: 1, padding:'15px', backgroundColor:'#2d1a00', border:'2px solid #fd7e14', borderRadius:'8px', color:'#fff', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>
                      Resume Golf Match
                  </button>
              )}
              {suspendedShanghai && (
                  <button onClick={resumeShanghai} style={{ flex: 1, padding:'15px', backgroundColor:'#0a2744', border:'2px solid #0d6efd', borderRadius:'8px', color:'#fff', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>
                      Resume Shanghai
                  </button>
              )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '25px', marginBottom: '30px' }}>
              <button onClick={start501} style={{ padding: '20px', backgroundColor: '#28a745', border: 'none', borderRadius: '12px', color: '#fff', fontWeight:'bold', fontSize:'28px', cursor: 'pointer' }}>Start 501</button>
              <button onClick={startGolf} style={{ padding: '20px', backgroundColor: '#fd7e14', border: 'none', borderRadius: '12px', color: '#fff', fontWeight:'bold', fontSize:'28px', cursor: 'pointer' }}>Start Golf</button>
              <button onClick={startShanghai} style={{ padding: '20px', backgroundColor: '#0d6efd', border: 'none', borderRadius: '12px', color: '#fff', fontWeight:'bold', fontSize:'28px', cursor: 'pointer' }}>Start Shanghai</button>
          </div>
          <div style={{ background: '#252525', padding: '25px', borderRadius: '12px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ flex: '1 1 100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' }}>
                        <span style={{ fontSize: '22px', color: '#aaa', fontWeight: 'bold' }}>Roster Size:</span>
                        {[2, 3, 4].map(num => (
                            <button key={num} onClick={() => setPlayerCount(num)} style={{ padding: '10px 20px', background: playerCount === num ? '#007bff' : '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>
                                {num} Players
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        {Array.from({ length: playerCount }).map((_, i) => (
                            <input 
                                key={i} 
                                type="text" 
                                value={playerNames[i] || ''} 
                                onChange={(e) => { const n = [...playerNames]; n[i] = e.target.value; setPlayerNames(n); }} 
                                style={{ padding: '15px', background: '#1e1e1e', color: '#fff', border: '1px solid #555', borderRadius: '8px', fontSize: '20px', width: '100%', boxSizing:'border-box' }} 
                                placeholder={`Player ${i+1}`} 
                            />
                        ))}
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{display: 'block', marginBottom: '10px', color: '#aaa', fontSize: '18px', fontWeight: 'bold'}}>Course Layout:</label>
                    <select onChange={(e) => setSelectedCourse(e.target.value)} value={selectedCourse} style={{ width: '100%', padding: '15px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', boxSizing:'border-box' }}>
                        <option value="paul">Psycho Sanctuary</option>
                        <option value="bernie">Mottly Reserve</option>
                    </select>
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{display: 'block', marginBottom: '10px', color: '#aaa', fontSize: '18px', fontWeight: 'bold'}}>Tournament Mode:</label>
                    <select onChange={(e) => setTournament(e.target.value)} value={tournament} style={{ width: '100%', padding: '15px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', boxSizing:'border-box' }}>
                        <option value="none">Casual (Single Round)</option>
                        <option value="masters">NR Masters (4 Rounds)</option>
                        <option value="classic">Currumbin Classic (4 Rounds)</option>
                    </select>
                </div>
            </div>
          </div>
          <div style={{ borderTop: '3px solid #333', paddingTop: '30px', marginTop: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ color: '#aaa', margin: 0, fontSize: '28px' }}>Hero Stats & Records</h3>
                <button onClick={() => setActiveMode('analytics')} style={{ padding: '12px 25px', background: '#6f42c1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>📊 Open Live Analytics</button>
            </div>
            {renderRecordsSummary()}
          </div>
          
          {/* DISCREET ADMIN FOOTER */}
          <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #222' }}>
              <span onClick={openAdmin} style={{ color: '#444', fontSize: '14px', cursor: 'pointer', letterSpacing: '1px' }}>System Admin</span>
          </div>
        </div>
      )}

      {/* -------------------- ANALYTICS DASH -------------------- */}
      {activeMode === 'analytics' && (
        <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', background: '#1e1e1e', padding: '30px', borderRadius: '16px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '2px solid #333', paddingBottom: '20px' }}>
                <button onClick={() => { setActiveMode('menu'); setAutoRefresh(false); }} style={{ padding: '15px 30px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>← Back to Home Page</button>
                <h2 style={{ margin: 0, color: '#6f42c1', fontSize: '36px', letterSpacing: '2px' }}>LIVE ANALYTICS PROJECTOR</h2>
                <button onClick={() => setAutoRefresh(!autoRefresh)} style={{ padding: '15px 30px', background: autoRefresh ? '#28a745' : '#dc3545', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>Auto-Refresh: {autoRefresh ? 'ON' : 'OFF'}</button>
            </div>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
                <button onClick={() => setAnalTab('ledger')} style={{ flex: 1, padding: '15px', background: analTab === 'ledger' ? '#007bff' : '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>🏆 PGA Tour Ledger</button>
                <button onClick={() => setAnalTab('vault')} style={{ flex: 1, padding: '15px', background: analTab === 'vault' ? '#fd7e14' : '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>📂 Scorecard Vault</button>
            </div>

            {analTab === 'ledger' && (
                <div style={{ background: '#252525', padding: '30px', borderRadius: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, color: '#aaa', fontSize: '28px' }}>Lifetime Stats</h3>
                        <input type="text" placeholder="🔍 Search Player..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ padding: '15px 20px', borderRadius: '8px', border: 'none', background: '#1e1e1e', color: '#fff', fontSize: '20px', width: '300px' }} />
                    </div>
                    <table style={{ width: '100%', color: '#fff', textAlign: 'left', borderCollapse: 'collapse', fontSize: '20px' }}>
                        <thead>
                            <tr style={{borderBottom: '3px solid #444'}}>
                                <th style={{padding: '15px 10px'}}>Player</th>
                                <th style={{textAlign: 'center', color: '#007bff'}}>501 W</th>
                                <th style={{textAlign: 'center', color: '#007bff'}}>High Out</th>
                                <th style={{textAlign: 'center', color: '#fd7e14'}}>Golf W</th>
                                <th style={{textAlign: 'center', color: '#0d6efd'}}>SH W</th>
                                <th style={{textAlign: 'center', color: '#0d6efd'}}>SH</th>
                                <th style={{textAlign: 'center', color: '#0d6efd'}}>High SH</th>
                                <th style={{textAlign: 'center', color: '#0d6efd'}}>High visit</th>
                                <th style={{textAlign: 'center', color: '#0d6efd'}}>Dbls</th>
                                <th style={{textAlign: 'center', color: '#0d6efd'}}>Tpls</th>
                                <th style={{textAlign: 'center', color: '#6f42c1'}}>Albs</th>
                                <th style={{textAlign: 'center', color: '#ffc107'}}>Egls</th>
                                <th style={{textAlign: 'center', color: '#28a745'}}>Brds</th>
                                <th style={{textAlign: 'center', color: '#6c757d'}}>Pars</th>
                            </tr>
                        </thead>
                        <tbody>
                        {filteredLedger.length === 0 && <tr><td colSpan="14" style={{textAlign:'center', padding:'30px', color:'#666'}}>No records found.</td></tr>}
                        {filteredLedger.map(name => (
                            <tr key={name} style={{borderBottom: '1px solid #333'}}>
                                <td style={{padding: '15px 10px', fontWeight: 'bold'}}>{name}</td>
                                <td style={{textAlign: 'center'}}>{stats['501'].wins[name] || 0}</td>
                                <td style={{textAlign: 'center', fontWeight: 'bold'}}>{stats['501'].highOut[name] || '-'}</td>
                                <td style={{textAlign: 'center'}}>{stats.golf.wins[name] || 0}</td>
                                <td style={{textAlign: 'center', color: '#0d6efd'}}>{(stats.shanghai && stats.shanghai.wins && stats.shanghai.wins[name]) || 0}</td>
                                <td style={{textAlign: 'center', color: '#0d6efd'}}>{(stats.shanghai && stats.shanghai.shanghaiHits && stats.shanghai.shanghaiHits[name]) || 0}</td>
                                <td style={{textAlign: 'center', color: '#0d6efd', fontWeight: 'bold'}}>{(stats.shanghai && stats.shanghai.highGame && stats.shanghai.highGame[name]) || '-'}</td>
                                <td style={{textAlign: 'center', color: '#0d6efd', fontWeight: 'bold'}}>{(stats.shanghai && stats.shanghai.highVisit && stats.shanghai.highVisit[name]) || '-'}</td>
                                <td style={{textAlign: 'center', color: '#0d6efd'}}>{(stats.shanghai && stats.shanghai.doubles && stats.shanghai.doubles[name]) || 0}</td>
                                <td style={{textAlign: 'center', color: '#0d6efd'}}>{(stats.shanghai && stats.shanghai.triples && stats.shanghai.triples[name]) || 0}</td>
                                <td style={{textAlign: 'center', color: '#6f42c1', fontWeight: 'bold'}}>{stats.golf.albatrosses[name] || 0}</td>
                                <td style={{textAlign: 'center', color: '#ffc107', fontWeight: 'bold'}}>{stats.golf.eagles[name] || 0}</td>
                                <td style={{textAlign: 'center', color: '#28a745', fontWeight: 'bold'}}>{stats.golf.birdies[name] || 0}</td>
                                <td style={{textAlign: 'center', color: '#aaa'}}>{stats.golf.pars[name] || 0}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {analTab === 'vault' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {(!stats.scorecards || stats.scorecards.length === 0) && <div style={{ textAlign: 'center', padding: '50px', color: '#666', fontSize: '24px' }}>No rounds have been archived yet.</div>}
                    {stats.scorecards?.map(card => (
                        <div key={card.id} style={{ background: '#252525', borderRadius: '12px', borderLeft: '8px solid #fd7e14', overflow: 'hidden' }}>
                            <div onClick={() => setExpandedCard(expandedCard === card.id ? null : card.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', cursor: 'pointer', background: '#333' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>{card.date}</span>
                                    <span style={{ fontSize: '20px', color: '#fd7e14' }}>{COURSE_LAYOUTS[card.course].name}</span>
                                    <span style={{ fontSize: '18px', color: '#aaa', background: '#1e1e1e', padding: '5px 10px', borderRadius: '6px' }}>{TOURNAMENT_NAMES[card.tournament] || 'Casual Play'} {card.tournament !== 'none' && `(R${card.round})`}</span>
                                </div>
                                <span style={{ fontSize: '24px', color: '#aaa' }}>{expandedCard === card.id ? '▼ Hide Scorecard' : '▶ View Scorecard'}</span>
                            </div>
                            
                            {expandedCard === card.id && (
                                <div style={{ padding: '20px', overflowX: 'auto' }}>
                                    <table style={{ width: '100%', color: '#fff', borderCollapse: 'collapse', textAlign: 'center', fontSize: '18px' }}>
                                        <thead>
                                            <tr style={{ background: '#1e1e1e', borderBottom: '2px solid #444' }}>
                                                <th style={{ padding: '15px', textAlign: 'left' }}>Hole</th>
                                                {Array.from({length: 18}, (_, i) => i + 1).map(h => <th key={h} style={{ padding: '15px 5px', color: '#aaa' }}>{h}</th>)}
                                                <th style={{ padding: '15px', color: '#fd7e14' }}>TOT</th>
                                            </tr>
                                            <tr style={{ borderBottom: '2px solid #444' }}>
                                                <th style={{ padding: '15px', textAlign: 'left', color: '#888' }}>Par</th>
                                                {Array.from({length: 18}, (_, i) => i + 1).map(h => <th key={h} style={{ padding: '15px 5px', color: '#888' }}>{COURSE_LAYOUTS[card.course].pars[h]}</th>)}
                                                <th style={{ padding: '15px', color: '#888' }}>72</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {card.players.map((p, idx) => (
                                                <tr key={p.name} style={{ borderBottom: '1px solid #333', background: idx % 2 === 0 ? 'transparent' : '#1e1e1e' }}>
                                                    <td style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>{p.name}</td>
                                                    {Array.from({length: 18}, (_, i) => i + 1).map(h => (
                                                        <td key={h} style={{ padding: '10px 5px' }}>
                                                            <div style={{ background: getHoleColor(p.scores[h]), borderRadius: '4px', padding: '5px', color: p.scores[h] === undefined ? 'transparent' : p.scores[h] === -2 ? '#000' : '#fff', fontWeight: 'bold' }}>
                                                                {p.scores[h] === undefined ? '-' : formatScore(p.scores[h])}
                                                            </div>
                                                        </td>
                                                    ))}
                                                    <td style={{ padding: '15px', fontWeight: 'bold', color: '#fd7e14' }}>{formatScore(p.totalRelative)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
      )}

      {/* -------------------- 501 ARENA -------------------- */}
      {activeMode === '501' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', width: '100%', flexGrow: 1, boxSizing: 'border-box', minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', minHeight: 0 }}>
                <div style={{ background: '#1e1e1e', padding: '15px 20px', borderRadius: '12px', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <h3 style={{color:'#aaa', margin: '0 0 10px 0', fontSize: '20px', borderBottom: '2px solid #333', paddingBottom: '10px'}}>501 Status</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-evenly', gap: '8px', minHeight: 0 }}>
                        {p501.map((p, idx) => {
                            const avg = threeDartAverage(history501, idx);
                            return (
                            <div key={p.id} style={{display:'flex', justifyContent:'space-between', alignItems: 'center', padding:'10px 20px', background: idx === active501Idx ? '#1a3a5a' : '#252525', border: idx === active501Idx ? '3px solid #007bff' : '3px solid transparent', borderRadius: '10px'}}>
                                <div style={{display:'flex', flexDirection:'column', gap: 2}}>
                                  <span style={{fontSize: '24px', fontWeight: idx === active501Idx ? 'bold' : 'normal'}}>{p.name} {idx === active501Idx && '🎯'}</span>
                                  <span style={{fontSize: '13px', color: '#8ab4f8'}}>{avg != null ? `3DA ${avg}` : '3DA —'}</span>
                                </div>
                                <span style={{fontSize: '48px', fontWeight: 'bold', fontFamily: 'monospace', color: idx === active501Idx ? '#007bff' : '#fff'}}>{p.currentScore}</span>
                            </div>
                            );
                        })}
                    </div>
                </div>
                <div style={{ background: '#1e1e1e', padding: '15px', borderRadius: '12px', minHeight: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: checkoutRoutes.length > 0 ? '4px dashed #28a745' : '4px solid #333' }}>
                    <span style={{ fontSize: '16px', color: '#888', textTransform: 'uppercase', marginBottom: '5px', letterSpacing: '1px' }}>{checkoutRoutes.length > 0 ? "Tactical Checkout Routes" : "Out of Range"}</span>
                    {checkoutRoutes.length > 0 ? (
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>{checkoutRoutes[0]}</div>
                            {checkoutRoutes[1] && <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>{checkoutRoutes[1]}</div>}
                        </div>
                    ) : <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#555' }}>Awaiting Range</div>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                    <button onClick={undo501} style={{padding: '15px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer'}}>Undo</button>
                    <button onClick={suspend501} style={{padding: '15px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer'}}>Pause</button>
                    <button onClick={() => showConfirm("Abort Match", "Are you sure? Progress will be lost.", () => { clearSuspended501(); setActiveMode('menu'); })} style={{padding: '15px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer'}}>Abort</button>
                </div>
            </div>
            <div style={{ background: '#1e1e1e', padding: '20px', borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '22px', color: '#aaa' }}>Entry for: <strong style={{ color: '#007bff' }}>{p501[active501Idx]?.name}</strong></span>
                    <div style={{ fontSize: '14px', color: '#666', marginTop: 4 }}>
                      Room {roomId} · {cameraConnected ? <span style={{ color: '#28a745', fontWeight: 700 }}>Camera connected</span> : <span style={{ color: '#888' }}>Camera offline</span>} · phone can fill darts
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, margin: '10px 0' }}>
                      {[0,1,2].map(i => (
                        <button key={i} onClick={() => setVisitEditIdx(i)} style={{ padding: 8, borderRadius: 8, border: visitEditIdx === i ? '2px solid #28a745' : '2px solid #333', background: visit501[i] ? '#1a3a5a' : '#111', color: '#fff', cursor: 'pointer' }}>
                          <div style={{ fontSize: 11, color: '#888' }}>D{i+1}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'monospace' }}>{formatDart(visit501[i])}</div>
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '72px', background: '#000', borderRadius: '12px', padding: '8px', margin: '8px 0', fontFamily: 'monospace', minHeight: '88px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#28a745', letterSpacing: '5px' }}>{visit501.some(Boolean) ? visitTotal(visit501) : (input501 || '0')}</div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
                      <button onClick={() => { setVisit501(emptyVisit()); getSocket().emit('visit:clear', { roomId }); }} style={{ padding: '8px 12px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Clear darts</button>
                      <button onClick={confirmVisit501} disabled={!visit501.some(Boolean)} style={{ padding: '8px 16px', background: visit501.some(Boolean) ? '#28a745' : '#333', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Confirm camera visit</button>
                      <a href={`/camera?room=${encodeURIComponent(roomId)}`} style={{ padding: '8px 12px', background: '#0d6efd', color: '#fff', borderRadius: 8, fontWeight: 700, textDecoration: 'none' }}>Camera</a>
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', flexGrow: 1 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <button key={n} onClick={() => { if(input501.length < 3) setInput501(input501 + n) }} style={{ fontSize: '36px', backgroundColor: '#2d2d2d', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>{n}</button>)}
                    <button onClick={() => setInput501('')} style={{ fontSize: '28px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>CLR</button>
                    <button onClick={() => { if(input501.length < 3) setInput501(input501 + '0') }} style={{ fontSize: '36px', backgroundColor: '#2d2d2d', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>0</button>
                    <button onClick={() => submit501Score(input501 || '0')} style={{ fontSize: '28px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>ENT</button>
                </div>
            </div>
        </div>
      )}


      {/* -------------------- SHANGHAI ARENA -------------------- */}
      {activeMode === 'shanghai' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', width: '100%', flexGrow: 1, boxSizing: 'border-box', minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', minHeight: 0 }}>
                <div style={{ background: '#1e1e1e', padding: '15px 20px', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', color: '#888', letterSpacing: '2px', textTransform: 'uppercase' }}>Target</div>
                    <div style={{ fontSize: shRound.label.length > 4 ? '46px' : '88px', fontWeight: '900', color: '#0d6efd', fontFamily: 'monospace', lineHeight: 1.1, letterSpacing: shRound.label.length > 4 ? '1px' : 'normal' }}>{shRound.label}</div>
                    <div style={{ fontSize: '16px', color: '#aaa', marginTop: '6px' }}>{shHelpText(shRound)}</div>
                </div>
                <div style={{ background: '#1e1e1e', padding: '15px 20px', borderRadius: '12px', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <h3 style={{color:'#aaa', margin: '0 0 10px 0', fontSize: '20px', borderBottom: '2px solid #333', paddingBottom: '10px'}}>Shanghai totals</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-evenly', gap: '8px', minHeight: 0 }}>
                        {shPlayers.map((p, idx) => (
                            <div key={p.id} style={{display:'flex', justifyContent:'space-between', alignItems: 'center', padding:'10px 20px', background: idx === activeShIdx ? '#0a2744' : '#252525', border: idx === activeShIdx ? '3px solid #0d6efd' : '3px solid transparent', borderRadius: '10px'}}>
                                <span style={{fontSize: '24px', fontWeight: idx === activeShIdx ? 'bold' : 'normal'}}>{p.name} {idx === activeShIdx && '🎯'}</span>
                                <span style={{fontSize: '48px', fontWeight: 'bold', fontFamily: 'monospace', color: idx === activeShIdx ? '#0d6efd' : '#fff'}}>{p.total}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                    <button onClick={undoShanghai} style={{padding: '15px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer'}}>Undo</button>
                    <button onClick={suspendShanghai} style={{padding: '15px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer'}}>Pause</button>
                    <button onClick={() => setActiveMode('menu')} style={{padding: '15px', background: '#333', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer'}}>Menu</button>
                    <button onClick={() => showConfirm("Abort Match", "Are you sure? Progress will be lost.", () => { clearSuspendedShanghai(); setActiveMode('menu'); })} style={{padding: '15px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer'}}>Abort</button>
                    <a href={`/camera?room=${encodeURIComponent(roomId)}`} style={{ padding: '15px 18px', background: '#28a745', color: '#fff', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', textDecoration: 'none' }}>Camera</a>
                    <span style={{ fontSize: 14, color: '#888' }}>Room {roomId} · {cameraConnected ? <span style={{ color: '#28a745', fontWeight: 700 }}>Camera connected</span> : 'Camera offline'}</span>
                </div>
            </div>
            <div style={{ background: '#1e1e1e', padding: '20px', borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '22px', color: '#aaa' }}>Throw: <strong style={{ color: '#0d6efd' }}>{shPlayers[activeShIdx]?.name}</strong></span>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', margin: '12px 0 8px' }}>
                        {[0,1,2].map(i => (
                            <div key={i} style={{ width: '80px', height: '60px', borderRadius: '8px', background: '#000', border: '2px solid #0d6efd', color: '#0d6efd', fontSize: '28px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {shDarts[i] ? (SH_DART_LABEL[shDarts[i]] || shDarts[i]) : '·'}
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: '36px', fontFamily: 'monospace', fontWeight: 'bold', color: '#0d6efd', marginBottom: '4px' }}>
                        {(() => {
                            const cur = shPlayers[activeShIdx]?.total ?? 20;
                            if (shRound.type === 'anyD' || shRound.type === 'anyT') {
                                const typed = parseInt(inputSh, 10) || 0;
                                if (!typed) return `${cur}  ·  pending 0`;
                                if (!shTypedScoreOk(shRound.type, typed)) return `${typed} not a ${shRound.type === 'anyD' ? 'double' : 'triple'} score`;
                                return `${cur}+${typed}=${cur + typed}`;
                            }
                            const parts = shDarts.filter(isShHit).map(d => shDartPts(d, shTargetN()));
                            if (parts.length === 0) return `${cur}  ·  pending 0`;
                            const sum = parts.reduce((a, b) => a + b, 0);
                            return `${cur}+${parts.join('+')}=${cur + sum}`;
                        })()}
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexGrow: 1, minHeight: 0 }}>
                    {shRound.type === 'num' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', flex: '1.1 1 0', minHeight: 0 }}>
                            <button onClick={() => addShanghaiDart('M')} style={{ padding: '18px 12px', background: '#333', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>MISS</button>
                            <button onClick={() => addShanghaiDart('S')} style={{ padding: '18px 12px', background: '#198754', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>SINGLE</button>
                            <button onClick={() => addShanghaiDart('D')} style={{ padding: '18px 12px', background: '#fd7e14', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>DOUBLE</button>
                            <button onClick={() => addShanghaiDart('T')} style={{ padding: '18px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>TRIPLE</button>
                        </div>
                    )}
                    {(shRound.type === 'anyD' || shRound.type === 'anyT') && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flexGrow: 1, minHeight: 0 }}>
                            <button onClick={() => addShanghaiDart('M')} style={{ padding: '18px 16px', background: '#333', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '32px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px', flex: '0 0 auto' }}>MISS</button>
                            <div style={{ fontSize: '18px', color: '#aaa', textAlign: 'center' }}>Type the score from your {shRound.type === 'anyD' ? 'doubles' : 'triples'}, then ENT</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', flexGrow: 1, minHeight: 0 }}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                    <button key={n} onClick={() => { if (inputSh.length < 3) setInputSh(inputSh + String(n)); }} style={{ fontSize: '42px', backgroundColor: '#2d2d2d', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold' }}>{n}</button>
                                ))}
                                <div />
                                <button onClick={() => { if (inputSh.length < 3) setInputSh(inputSh + '0'); }} style={{ fontSize: '42px', backgroundColor: '#2d2d2d', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold' }}>0</button>
                                <div />
                            </div>
                        </div>
                    )}
                    {shRound.type === 'outer' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', flexGrow: 1, minHeight: 0 }}>
                            <button onClick={() => addShanghaiDart('M')} style={{ padding: '18px 12px', background: '#333', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>MISS</button>
                            <button onClick={() => addShanghaiDart('O')} style={{ padding: '18px 12px', background: '#198754', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>25</button>
                            <button onClick={() => addShanghaiDart('B')} style={{ padding: '18px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>BULL</button>
                            <div />
                        </div>
                    )}
                    {shRound.type === 'bull' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', flexGrow: 1, minHeight: 0 }}>
                            <button onClick={() => addShanghaiDart('M')} style={{ padding: '18px 12px', background: '#333', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>MISS</button>
                            <button onClick={() => addShanghaiDart('B')} style={{ padding: '18px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '34px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>BULL</button>
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', flex: '1 1 0', minHeight: '110px' }}>
                        <button onClick={clearPendingShanghaiDart} style={{ padding: '28px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '16px', fontSize: '48px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>CLR</button>
                        <button onClick={submitShanghaiVisit} style={{ padding: '28px 16px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '16px', fontSize: '48px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }}>ENT</button>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* -------------------- GOLF ARENA -------------------- */}
      {activeMode === 'golf' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px', width: '100%', flexGrow: 1, boxSizing: 'border-box', minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', minHeight: 0 }}>
                <div style={{ background: '#1e1e1e', padding: '20px', borderRadius: '12px', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #333', paddingBottom: '15px', marginBottom: '15px' }}>
                        <div>
                            <h3 style={{ margin: 0, color: '#fd7e14', fontSize: '28px' }}>{golfPlayoff ? `PLAYOFF | Hole ${currentHole}` : `${tournament !== 'none' ? `Round ${currentRound}` : 'Casual'} | Hole ${currentHole}`}</h3>
                            <p style={{ margin: '5px 0 0 0', color: '#aaa', fontSize: '18px' }}>{COURSE_LAYOUTS[selectedCourse].name}{golfPlayoff ? ' · Sudden Death' : ''} · {cameraConnected ? <span style={{ color: '#28a745', fontWeight: 700 }}>Camera connected</span> : 'phone can confirm hole'}</p>
                        </div>
                        <h3 style={{ margin: 0, color: '#aaa', fontSize: '28px' }}>Par {activePar}</h3>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-evenly', gap: '10px', minHeight: 0 }}>
                        {golfPlayers.map((p, idx) => {
                            const liveTourTotal = getLiveTotal(p);
                            const isActive = idx === activeGolfIdx;
                            const inPlayoff = !golfPlayoff || playoffNames.includes(p.name);
                            return (
                                <div key={p.id} style={{padding:'12px 20px', background: isActive ? '#2d1a00' : '#252525', border: isActive ? '3px solid #fd7e14' : '3px solid transparent', borderRadius: '10px', opacity: inPlayoff ? 1 : 0.4}}>
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems: 'center'}}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <span style={{ fontSize: '20px', color: '#aaa', fontWeight: 'bold' }}>{golfPlayoff ? (inPlayoff ? 'Playoff' : 'Out') : `Tee ${idx + 1}`}</span>
                                            <span style={{fontSize: '26px', fontWeight: isActive ? 'bold' : 'normal'}}>{p.name} {isActive && '⛳'}</span>
                                        </div>
                                        <span style={{fontSize: '36px', fontWeight: 'bold', fontFamily: 'monospace', color: tournament !== 'none' ? '#fd7e14' : '#fff'}}>Total: {formatScore(liveTourTotal)}</span>
                                    </div>
                                    {tournament !== 'none' && (
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #444'}}>
                                            <span style={{fontSize:'18px', color:'#fff', fontWeight: 'bold'}}>Cur Round: <span style={{color: '#aaa'}}>{formatScore(p.totalRelative)}</span></span>
                                            {currentRound > 1 && <span style={{fontSize:'16px', color:'#888'}}>Prior: {p.roundHistory.map(formatScore).join(', ')}</span>}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                    <button onClick={undoGolf} style={{padding: '15px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer'}}>Undo</button>
                    <button onClick={suspendGolf} style={{padding: '15px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer'}}>Pause</button>
                    <button onClick={() => showConfirm("Abort Match", "Are you sure? Progress will be lost.", () => { clearSuspendedGolf(); setActiveMode('menu'); })} style={{padding: '15px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer'}}>Abort</button>
                </div>
            </div>
            <div style={{ background: '#1e1e1e', padding: '20px', borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <span style={{ fontSize: '26px', color: '#aaa' }}>On the Tee: <strong style={{ color: '#fd7e14' }}>{golfPlayers[activeGolfIdx]?.name}</strong></span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', flexGrow: 1 }}>
                    {GOLF_RESULTS.map(r => {
                        const isValid = activePar + r.relativeScore > 0;
                        return (
                            <button 
                                key={r.label} 
                                onClick={() => isValid && submitGolfScore(r.relativeScore)} 
                                disabled={!isValid}
                                style={{ 
                                    background: isValid ? r.color : '#333', 
                                    opacity: isValid ? 1 : 0.4,
                                    border:'none', 
                                    borderRadius: '10px', 
                                    padding:'20px', 
                                    fontSize:'24px', 
                                    fontWeight:'bold', 
                                    color: isValid ? r.text : '#777', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    cursor: isValid ? 'pointer' : 'not-allowed', 
                                    boxShadow: isValid ? '0 4px 8px rgba(0,0,0,0.3)' : 'none' 
                                }}
                            >
                                {r.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
      )}

      {/* -------------------- TOURNAMENT BROADCAST -------------------- */}
      {postRoundData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(18, 18, 18, 0.98)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: '40px' }}>
            <div style={{ width: '100%', maxWidth: '900px' }}>
                <h1 style={{ color: '#fd7e14', fontSize: '56px', textAlign: 'center', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '4px' }}>
                    {postRoundData.isTournamentEnd ? 'Tournament Champion' : `Round ${postRoundData.round} Complete`}
                </h1>
                <h2 style={{ color: '#aaa', fontSize: '28px', textAlign: 'center', marginBottom: '40px' }}>
                    {postRoundData.isTournamentEnd ? TOURNAMENT_NAMES[tournament] : COURSE_LAYOUTS[postRoundData.course].name}{postRoundData.playoffWinner ? ' · Won in Playoff' : ''}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '40px' }}>
                    {[...postRoundData.players].sort((a,b) => {
                        if (postRoundData.playoffWinner) {
                            if (a.name === postRoundData.playoffWinner) return -1;
                            if (b.name === postRoundData.playoffWinner) return 1;
                        }
                        const fa = tournament !== 'none' ? a.tourTotal + a.totalRelative : a.totalRelative;
                        const fb = tournament !== 'none' ? b.tourTotal + b.totalRelative : b.totalRelative;
                        return fa - fb;
                    }).map((p, idx) => {
                        const finalTotal = tournament !== 'none' ? p.tourTotal + p.totalRelative : p.totalRelative;
                        const isChamp = idx === 0 && postRoundData.isTournamentEnd;
                        return (
                            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isChamp ? 'linear-gradient(90deg, #fd7e14, #ffc107)' : '#252525', padding: '20px 30px', borderRadius: '10px', border: idx === 0 ? '2px solid #ffc107' : '1px solid #444' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <span style={{ fontSize: '28px', color: isChamp ? '#000' : '#aaa', fontWeight: 'bold' }}>{idx + 1}</span>
                                    <span style={{ fontSize: '32px', fontWeight: 'bold', color: isChamp ? '#000' : '#fff' }}>{p.name}{postRoundData.playoffWinner === p.name ? ' (Playoff)' : ''}</span>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '36px', fontWeight: 'bold', fontFamily: 'monospace', color: isChamp ? '#000' : '#fff' }}>Total: {formatScore(finalTotal)}</div>
                                    {tournament !== 'none' && <div style={{ fontSize: '18px', color: isChamp ? '#333' : '#aaa', marginTop: '5px' }}>R{postRoundData.round}: {formatScore(p.totalRelative)}</div>}
                                </div>
                            </div>
                        )
                    })}
                </div>
                <button onClick={handlePostRoundContinue} style={{ width: '100%', padding: '25px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '28px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,123,255,0.4)' }}>
                    {postRoundData.isTournamentEnd ? 'Return to Clubhouse' : 'Advance to Next Round'}
                </button>
            </div>
        </div>
      )}


      {adminOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9800, padding: '20px' }}>
            <div style={{ background: '#1e1e1e', padding: '32px', borderRadius: '12px', border: '2px solid #333', maxWidth: '640px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2 style={{ color: '#aaa', margin: '0 0 8px', fontSize: '28px' }}>System Admin</h2>
                <p style={{ color: '#888', margin: '0 0 24px', fontSize: '16px' }}>Clear one thing at a time. Full wipe is still here if you need it.</p>
                <div style={{ margin: '0 0 10px', color: '#0d6efd', fontSize: '18px', fontWeight: 'bold' }}>Shanghai games</div>
                {(((stats.shanghai && stats.shanghai.games) || []).length === 0) && <div style={{ color: '#666', padding: '10px 0 16px' }}>No Shanghai games saved yet.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                    {[...((stats.shanghai && stats.shanghai.games) || [])].reverse().map(g => (
                        <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#252525', padding: '12px 14px', borderRadius: '8px' }}>
                            <span style={{ color: '#ddd', fontSize: '16px' }}>{g.date} · {g.title}</span>
                            <button onClick={() => deleteShanghaiGame(g.id)} style={{ padding: '8px 14px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Delete</button>
                        </div>
                    ))}
                </div>
                <button onClick={clearShanghaiRecords} style={{ width: '100%', padding: '14px', marginBottom: '18px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>Clear all Shanghai games</button>
                <div style={{ margin: '8px 0 10px', color: '#aaa', fontSize: '18px', fontWeight: 'bold' }}>Golf scorecards</div>
                {(stats.scorecards || []).length === 0 && <div style={{ color: '#666', padding: '10px 0 16px' }}>No golf rounds archived.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                    {(stats.scorecards || []).map(card => (
                        <div key={card.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#252525', padding: '12px 14px', borderRadius: '8px' }}>
                            <span style={{ color: '#ddd', fontSize: '16px' }}>{card.date} · {COURSE_LAYOUTS[card.course]?.name || card.course}</span>
                            <button onClick={() => deleteGolfScorecard(card.id)} style={{ padding: '8px 14px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Delete</button>
                        </div>
                    ))}
                </div>
                <button onClick={resetEntireDatabase} style={{ width: '100%', padding: '14px', marginBottom: '12px', background: '#5c1a1a', color: '#fff', border: '1px solid #dc3545', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>Wipe entire database</button>
                <button onClick={() => setAdminOpen(false)} style={{ width: '100%', padding: '14px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>Close</button>
            </div>
        </div>
      )}

      {/* -------------------- MODALS -------------------- */}
      {modal.isOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: '#1e1e1e', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '2px solid #333', maxWidth: '500px', width: '90%', boxShadow: '0 15px 40px rgba(0,0,0,0.8)' }}>
                <h2 style={{color: modal.type === 'confirm' ? '#dc3545' : '#007bff', marginBottom: '20px', fontSize: '32px'}}>{modal.title}</h2>
                <p style={{fontSize: '20px', marginBottom: '30px', color: '#ddd', lineHeight: '1.4'}}>{modal.message}</p>
                <div style={{display: 'flex', gap: '20px', justifyContent: 'center'}}>
                    {modal.type === 'confirm' && <button onClick={closeModal} style={{ padding: '15px 30px', background: '#333', color:'#fff', borderRadius:'8px', border: 'none', fontSize: '18px', cursor: 'pointer', flex: 1, fontWeight: 'bold' }}>Cancel</button>}
                    <button onClick={() => { if(modal.onConfirm) modal.onConfirm(); closeModal(); }} style={{ padding: '15px 30px', background: modal.type === 'confirm' ? '#dc3545' : '#007bff', color:'#fff', borderRadius:'8px', border: 'none', fontSize: '18px', cursor: 'pointer', flex: 1, fontWeight: 'bold' }}>{modal.type === 'confirm' ? 'Confirm' : 'OK'}</button>
                </div>
            </div>
        </div>
      )}

      {/* -------------------- SHANGHAI SPLASH -------------------- */}
      {shanghaiSplash && (
        <div
          role="dialog"
          aria-live="assertive"
          aria-label={`Shanghai! ${shanghaiSplash.name} on ${shanghaiSplash.target}`}
          onClick={dismissShanghaiSplash}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            cursor: shanghaiDismissReady ? 'pointer' : 'default',
            background: 'radial-gradient(ellipse at 50% 38%, #3a0048 0%, #050508 68%)',
            animation: 'shWash 1.6s ease-in-out infinite'
          }}
        >
          <style>{`
            @keyframes shNeonFlicker {
              0%, 18%, 22%, 25%, 53%, 57%, 100% { opacity: 1; }
              20%, 24%, 55% { opacity: 0.28; }
            }
            @keyframes shGlowPulse {
              0%, 100% {
                text-shadow:
                  0 0 8px #fff,
                  0 0 18px #ff2bd6,
                  0 0 36px #ff2bd6,
                  0 0 72px #00f0ff,
                  0 0 110px #ffd700;
              }
              50% {
                text-shadow:
                  0 0 16px #fff,
                  0 0 32px #00f0ff,
                  0 0 64px #ff2bd6,
                  0 0 120px #ffd700,
                  0 0 180px #00f0ff;
              }
            }
            @keyframes shStrobeWord {
              0%, 100% { color: #ff2bd6; }
              25% { color: #00f0ff; }
              50% { color: #ffd700; }
              75% { color: #ffffff; }
            }
            @keyframes shSlam {
              0% { transform: scale(3.2) rotate(-10deg); opacity: 0; filter: blur(16px); }
              40% { transform: scale(0.9) rotate(3deg); opacity: 0.85; filter: blur(0); }
              62% { transform: scale(1.12) rotate(-2deg); opacity: 0.7; }
              100% { transform: scale(1.04) rotate(0deg); opacity: 0.28; }
            }
            @keyframes shSweep {
              0% { transform: translateX(-140%) skewX(-18deg); opacity: 0; }
              18% { opacity: 1; }
              50% { transform: translateX(20%) skewX(-18deg); opacity: 0.95; }
              100% { transform: translateX(160%) skewX(-18deg); opacity: 0; }
            }
            @keyframes shBarScroll {
              0% { background-position: 0 0; }
              100% { background-position: 0 220px; }
            }
            @keyframes shSpark {
              0% { transform: translateY(20vh) scale(1); opacity: 1; }
              100% { transform: translateY(-90vh) scale(0.15); opacity: 0; }
            }
            @keyframes shNamePulse {
              0%, 100% {
                color: #ffffff;
                text-shadow: 0 0 12px #00f0ff, 0 0 28px #00f0ff, 0 0 56px #ff2bd6, 0 0 90px #ffd700;
              }
              33% {
                color: #ff2bd6;
                text-shadow: 0 0 16px #ff2bd6, 0 0 40px #ffd700, 0 0 80px #00f0ff, 0 0 120px #ff2bd6;
              }
              66% {
                color: #ffd700;
                text-shadow: 0 0 16px #ffd700, 0 0 40px #00f0ff, 0 0 80px #ff2bd6, 0 0 120px #ffd700;
              }
            }
            @keyframes shWash {
              0%, 100% { background: radial-gradient(ellipse at 50% 38%, #3a0048 0%, #050508 68%); }
              50% { background: radial-gradient(ellipse at 48% 42%, #00334a 0%, #050508 70%); }
            }
            @keyframes shHintIn {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'repeating-linear-gradient(0deg, transparent, transparent 16px, rgba(255,255,255,0.045) 16px, rgba(255,255,255,0.045) 18px)',
            animation: 'shBarScroll 1.1s linear infinite',
            mixBlendMode: 'screen'
          }} />

          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: '38%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), rgba(0,240,255,0.55), rgba(255,215,0,0.35), rgba(255,43,214,0.4), transparent)',
            animation: 'shSweep 2.1s ease-in-out infinite',
            pointerEvents: 'none',
            mixBlendMode: 'screen'
          }} />

          {[
            { left: '8%', delay: '0s', color: '#ff2bd6' },
            { left: '16%', delay: '0.3s', color: '#00f0ff' },
            { left: '24%', delay: '0.7s', color: '#ffd700' },
            { left: '33%', delay: '0.15s', color: '#fff' },
            { left: '41%', delay: '0.9s', color: '#ff2bd6' },
            { left: '50%', delay: '0.45s', color: '#00f0ff' },
            { left: '58%', delay: '1.1s', color: '#ffd700' },
            { left: '67%', delay: '0.2s', color: '#fff' },
            { left: '75%', delay: '0.8s', color: '#00f0ff' },
            { left: '83%', delay: '0.55s', color: '#ff2bd6' },
            { left: '91%', delay: '0.05s', color: '#ffd700' },
            { left: '12%', delay: '1.4s', color: '#00f0ff' },
            { left: '46%', delay: '1.7s', color: '#ff2bd6' },
            { left: '72%', delay: '1.25s', color: '#ffd700' },
            { left: '88%', delay: '1.55s', color: '#fff' }
          ].map((s, i) => (
            <div key={i} style={{
              position: 'absolute',
              left: s.left,
              bottom: '8%',
              width: i % 3 === 0 ? '10px' : '6px',
              height: i % 3 === 0 ? '10px' : '6px',
              borderRadius: '50%',
              background: s.color,
              boxShadow: `0 0 10px ${s.color}, 0 0 22px ${s.color}`,
              animation: `shSpark 1.8s linear ${s.delay} infinite`,
              pointerEvents: 'none'
            }} />
          ))}

          <div style={{ position: 'relative', width: '100%', textAlign: 'center', padding: '0 4vw', zIndex: 1 }}>
            <div style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '-6%',
              fontFamily: 'Impact, system-ui, sans-serif',
              fontSize: 'clamp(72px, 18vw, 220px)',
              fontWeight: 900,
              letterSpacing: '0.08em',
              lineHeight: 0.9,
              color: '#ffd700',
              textShadow: '0 0 24px #ffd700, 0 0 60px #ff2bd6',
              animation: 'shSlam 1.15s cubic-bezier(0.2, 1.4, 0.3, 1) forwards',
              pointerEvents: 'none',
              userSelect: 'none'
            }}>SHANGHAI</div>

            <div style={{
              position: 'relative',
              fontFamily: 'Impact, system-ui, sans-serif',
              fontSize: 'clamp(72px, 18vw, 220px)',
              fontWeight: 900,
              letterSpacing: '0.08em',
              lineHeight: 0.9,
              animation: 'shNeonFlicker 1.7s infinite, shGlowPulse 1.35s ease-in-out infinite, shStrobeWord 2.2s linear infinite',
              userSelect: 'none'
            }}>SHANGHAI</div>

            <div style={{
              marginTop: '18px',
              fontFamily: 'Impact, system-ui, sans-serif',
              fontSize: 'clamp(56px, 12vw, 140px)',
              fontWeight: 900,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              lineHeight: 1,
              animation: 'shNamePulse 1.55s ease-in-out infinite',
              userSelect: 'none'
            }}>{shanghaiSplash.name}</div>

            <div style={{
              marginTop: '22px',
              fontFamily: 'system-ui, sans-serif',
              fontSize: 'clamp(22px, 3.6vw, 42px)',
              fontWeight: 800,
              color: '#ffffff',
              letterSpacing: '0.22em',
              textShadow: '0 0 10px #00f0ff, 0 0 24px #ff2bd6',
              userSelect: 'none'
            }}>S + D + T&nbsp;&nbsp;·&nbsp;&nbsp;TARGET {shanghaiSplash.target}</div>

            <div style={{
              marginTop: '36px',
              minHeight: '48px',
              fontFamily: 'system-ui, sans-serif',
              fontSize: 'clamp(20px, 2.6vw, 32px)',
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: shanghaiDismissReady ? '#ffd700' : 'transparent',
              textShadow: shanghaiDismissReady ? '0 0 12px #ffd700, 0 0 28px #ff2bd6' : 'none',
              animation: shanghaiDismissReady ? 'shHintIn 0.4s ease-out' : 'none',
              userSelect: 'none'
            }}>Back to clubhouse · tap anywhere</div>
          </div>
        </div>
      )}
    </div>
  );
}
